import { useState, useEffect, useRef, useCallback } from 'react';
import FAKE_USERNAMES from './fakeUsernames';
import { CHARACTERS, WORLD_CITIES, getRandomCities, LEVELS, LEVEL_NAMES, JUMP_FORCE, PLAYER_SIZE, GAME_HEIGHT, GAME_WIDTH, PIPE_WIDTH, COIN_SIZE } from './gameConstants';
import { CharacterSelect } from './CharacterSelect';
import { LoginScreen } from './LoginScreen';
import { MatchmakingScreen } from './MatchmakingScreen';
import { GameOverScreen, Leaderboard } from './GameOverScreen';
import { GameHUD } from './GameHUD';
import { LevelEffects } from './LevelEffects';
import { PlayerDeadOverlay, RankingsScreen } from './RankingsScreen';
import { PlayWithFriendsScreen, RoomLobby, useRoom } from './PlayWithFriends';
import { GameEmojiBar, GameFloatingEmoji } from './GameEmojiBar';

// Preload critical assets immediately
const KIDS_PNG_URL = 'https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/zt3z55rg_1000048790.png';
const preloadKids = new Image();
preloadKids.src = KIDS_PNG_URL;

// Seeded PRNG for synchronized pipe generation in room multiplayer
// All players in the same room see identical pipe layouts
function createSeededRNG(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h) + seed.charCodeAt(i);
    h |= 0;
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

const MimiFlyGame = () => {
  // Game state
  const [gameState, setGameState] = useState('intro'); // 'intro', 'splash', 'characterSelect', 'matchmaking', 'start', 'playing', 'paused', 'rankings', 'outro', 'gameOver'
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0); // Real-time score for game loop (avoids stale closure)
  const [coins, setCoins] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('mimiHighScore') || '0');
  });

  // Ad state - death counter for interstitial ads
  const [deathCount, setDeathCount] = useState(() => {
    return parseInt(localStorage.getItem('mimiDeathCount') || '0');
  });
  const [showInterstitialAd, setShowInterstitialAd] = useState(false);
  const [showRevivePopup, setShowRevivePopup] = useState(false);
  const [reviveCountdown, setReviveCountdown] = useState(5);
  const hasRevivedRef = useRef(false); // max 1 revive per run
  const reviveTimerRef = useRef(null);
  const revivePendingRef = useRef(false); // true while revive popup is showing — pauses physics

  // Music state
  const [isMusicOn, setIsMusicOn] = useState(() => {
    return localStorage.getItem('mimiMusicOn') !== 'false'; // Default to true
  });

  // Level system
  const [currentLevel, setCurrentLevel] = useState(0);
  const currentLevelRef = useRef(0); // Real-time level for game loop
  const [showLevelUp, setShowLevelUp] = useState(false);
  const levels = LEVELS;
  const levelNames = LEVEL_NAMES;

  // Pro / Free model
  const [isPro, setIsPro] = useState(() => localStorage.getItem('mimiPro') === 'true');
  
  // Ad-unlock system: each feature can be temporarily unlocked by watching 1 ad
  // Characters stay unlocked for 5 games, then need re-watching
  const [adUnlocked, setAdUnlocked] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('mimiAdUnlocked') || '{}');
      return {
        juma: (saved.juma || 0) > 0,
        soni: (saved.soni || 0) > 0,
        sam: (saved.sam || 0) > 0,
        levels: false,
        settings: false,
      };
    } catch { return { juma: false, soni: false, sam: false, levels: false, settings: false }; }
  });
  const adGamesLeftRef = useRef(() => {
    try { return JSON.parse(localStorage.getItem('mimiAdUnlocked') || '{}'); } catch { return {}; }
  });
  
  const watchAdToUnlock = useCallback((feature) => {
    // Simulate ad viewing (replace with real ad SDK when Publisher ID is provided)
    return new Promise((resolve) => {
      const adDiv = document.createElement('div');
      adDiv.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.9);display:flex;flex-direction:column;align-items:center;justify-content:center;color:white;font-family:Fredoka,sans-serif;';
      adDiv.innerHTML = `
        <div style="text-align:center">
          <div style="font-size:48px;margin-bottom:16px">📺</div>
          <div style="font-size:22px;font-weight:bold;margin-bottom:8px">Watching Ad...</div>
          <div style="font-size:14px;opacity:0.6;margin-bottom:24px">Feature unlocking in 3 seconds</div>
          <div style="width:200px;height:6px;background:rgba(255,255,255,0.2);border-radius:3px;overflow:hidden">
            <div style="width:0%;height:100%;background:linear-gradient(90deg,#fbbf24,#f59e0b);border-radius:3px;transition:width 3s linear" id="ad-progress"></div>
          </div>
        </div>
      `;
      document.body.appendChild(adDiv);
      requestAnimationFrame(() => {
        const bar = document.getElementById('ad-progress');
        if (bar) bar.style.width = '100%';
      });
      setTimeout(() => {
        document.body.removeChild(adDiv);
        // Grant 5 games for character unlocks
        if (['juma', 'soni', 'sam'].includes(feature)) {
          const games = { ...adGamesLeftRef.current };
          games[feature] = 5;
          adGamesLeftRef.current = games;
          localStorage.setItem('mimiAdUnlocked', JSON.stringify(games));
        }
        setAdUnlocked(prev => ({ ...prev, [feature]: true }));
        resolve();
      }, 3000);
    });
  }, []);
  
  // Decrement ad-unlock game count after each game (5 games per ad-watch)
  const decrementAdUnlocks = useCallback(() => {
    const games = { ...adGamesLeftRef.current };
    let changed = false;
    ['juma', 'soni', 'sam'].forEach(char => {
      if (games[char] && games[char] > 0) {
        games[char]--;
        changed = true;
      }
    });
    if (changed) {
      adGamesLeftRef.current = games;
      localStorage.setItem('mimiAdUnlocked', JSON.stringify(games));
      setAdUnlocked(prev => ({
        ...prev,
        juma: (games.juma || 0) > 0,
        soni: (games.soni || 0) > 0,
        sam: (games.sam || 0) > 0,
      }));
    }
    // Always reset levels and settings per game
    setAdUnlocked(prev => ({ ...prev, levels: false, settings: false }));
  }, []);
  
  const resetAdUnlocks = useCallback(() => {
    setAdUnlocked({ juma: false, soni: false, sam: false, levels: false, settings: false });
    adGamesLeftRef.current = {};
    localStorage.removeItem('mimiAdUnlocked');
  }, []);
  
  const maxLevel = isPro ? 6 : (adUnlocked.levels ? 1 : 0);
  const maxLevelRef = useRef(maxLevel);
  maxLevelRef.current = maxLevel; // Always sync with latest value

  // Settings
  const [sfxOn, setSfxOn] = useState(() => localStorage.getItem('mimiSfx') !== 'false');
  const [introOn, setIntroOn] = useState(() => isPro ? localStorage.getItem('mimiIntro') !== 'false' : true);
  const [outroOn, setOutroOn] = useState(() => isPro ? localStorage.getItem('mimiOutro') !== 'false' : true);
  const [vibrationOn, setVibrationOn] = useState(() => localStorage.getItem('mimiVibration') !== 'false');
  
  // Leaderboard state
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  // Level-lock ad prompt state (pauses game when free user reaches locked level)
  const [showLevelAdPrompt, setShowLevelAdPrompt] = useState(false);
  
  // Level-specific settings (will be updated based on current level)
  const levelSettings = useRef({
    gravity: 0.3,
    pipeSpeed: 2,
    pipeGap: 250,
    pipeSpawnRate: 168,
    margin: 60
  });

  // Player state - using refs for game loop to avoid re-render cycles
  const playerYRef = useRef(250);
  const playerVelocityRef = useRef(0);
  const playerRotationRef = useRef(0);
  
  // Display state (triggers re-renders for visual updates)
  const [playerDisplay, setPlayerDisplay] = useState({ y: 250, rotation: 0 });

  // Game objects - using refs to avoid re-render cycles
  const pipesRef = useRef([]);
  const coinsListRef = useRef([]);
  const cloudsRef = useRef([]);
  const balloonPowerupsRef = useRef([]);
  const tornadoesRef = useRef([]); // Tornado hazards
  
  // Display state for game objects
  const [gameObjects, setGameObjects] = useState({ pipes: [], coins: [], clouds: [], balloons: [], tornadoes: [], cameraX: 0 });

  // Camera system — follows the player during boost/knockback
  const cameraXRef = useRef(0);

  // Animation frame reference
  const frameRef = useRef(0);
  const animationRef = useRef(null);
  const lastPipeFrame = useRef(0);
  const lastCoinFrame = useRef(0);
  const lastCloudFrame = useRef(0);
  const lastBalloonFrame = useRef(0);
  const lastTornadoFrame = useRef(0);
  const pipeCountInLevelRef = useRef(0); // tracks how many pipes spawned in current level
  const levelStartScoreRef = useRef(0); // score when current level started
  const balloonsSpawnedInLevelRef = useRef(0); // balloons spawned this level
  const tornadoesSpawnedInLevelRef = useRef(0); // tornadoes spawned this level

  // Balloon Boost state — forward rush through 4 pipes
  const hasBalloonBoostRef = useRef(false);
  const boostPipesPassedRef = useRef(0); // count pipes passed during boost
  const balloonBoostTimerRef = useRef(null);
  const [balloonBoostActive, setBalloonBoostActive] = useState(false);
  
  // Tornado knockback state — backward push
  const isTornadoKnockbackRef = useRef(false);
  const knockbackFramesRef = useRef(0);

  // Slide-left slow-down state (per-player horizontal dodge)
  const touchStartRef = useRef(null);
  const isSlowingRef = useRef(false);
  const [isSlowing, setIsSlowing] = useState(false);
  const slowStartTimeRef = useRef(0); // timestamp when slow-down started
  const MAX_SLOW_DURATION = 2000; // 2 seconds max
  const playerXRef = useRef(80); // Dynamic horizontal position (default 80, slides back to ~25)

  // Slide-down dive state (power dive)
  const isDivingRef = useRef(false);
  const [isDiving, setIsDiving] = useState(false);
  const isTouchSlideRef = useRef(false); // true when touch gesture is a slide (not a tap)

  // Character definitions imported from gameConstants.js

  // Selected character state
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  
  // Health system
  const [lives, setLives] = useState(3);
  const livesRef = useRef(3); // Sync ref for game loop decisions
  const isInvincibleRef = useRef(false);
  const invincibleTimerRef = useRef(null);
  const totalCoinsRef = useRef(0);
  const lastHealAtRef = useRef(0);

  // Game mode: 'solo' or 'fakeOnline'
  const [gameMode, setGameMode] = useState('solo');
  const gameModeRef = useRef('solo');
  
  // Auth state for online modes
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('mimiLoggedIn') === 'true';
  });
  const [showLoginScreen, setShowLoginScreen] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(() => {
    const saved = localStorage.getItem('mimiUser');
    return saved ? JSON.parse(saved) : null;
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check for OAuth callback (session_id in URL hash)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        // Exchange session_id for user data
        fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        })
          .then(res => res.json())
          .then(data => {
            if (data.email) {
              setIsLoggedIn(true);
              setLoggedInUser(data);
              localStorage.setItem('mimiLoggedIn', 'true');
              localStorage.setItem('mimiUser', JSON.stringify(data));
              setShowLoginScreen(false);
              // After successful login, proceed to matchmaking
              setTimeout(() => {
                proceedToMatchmakingRef.current?.();
              }, 100);
            }
          })
          .catch(() => {})
          .finally(() => {
            // Clean up URL hash
            window.history.replaceState(null, '', window.location.pathname);
            // Proceed to matchmaking
            setShowLoginScreen(false);
          });
      }
    }
  }, [API_URL]);

  // Deep link auto-join: check for ?join=MIMI-XXXX in URL
  const [deepLinkPending, setDeepLinkPending] = useState(() => {
    return new URLSearchParams(window.location.search).has('join');
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    if (!joinCode) return;

    // Clean URL immediately
    window.history.replaceState(null, '', window.location.pathname);

    // Fetch room info to find available characters
    const autoJoin = async () => {
      try {
        const infoRes = await fetch(`${API_URL}/api/rooms/info/${joinCode}`);
        const infoData = await infoRes.json();
        if (infoData.error) {
          alert(`Room ${joinCode} not found or game already started`);
          setDeepLinkPending(false);
          return;
        }

        const room = infoData.room;
        const takenChars = room.players.map(p => p.character);
        const allChars = ['mimi', 'juma', 'soni', 'sam'];
        const availableChars = allChars.filter(c => !takenChars.includes(c));
        if (availableChars.length === 0) {
          alert('Room is full!');
          setDeepLinkPending(false);
          return;
        }

        // Pick first available character
        const assignedCharId = availableChars[0];
        const charObj = CHARACTERS.find(c => c.id === assignedCharId);

        // Generate player ID and name
        const playerId = `p_${Math.random().toString(36).slice(2, 10)}`;
        const playerName = charObj?.name || 'Player';

        // Join the room
        const joinRes = await fetch(`${API_URL}/api/rooms/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: joinCode,
            name: playerName,
            character: assignedCharId,
            player_id: playerId,
          }),
        });
        const joinData = await joinRes.json();
        if (joinData.error) {
          alert(joinData.error);
          setDeepLinkPending(false);
          return;
        }

        // Set character and go straight to lobby — skip intro/splash entirely
        setSelectedCharacter(charObj);
        setRoomCode(joinData.code);
        setRoomPlayerId(playerId);
        setRoomScreen('lobby');
        setGameState('characterSelect');
        setDeepLinkPending(false);
      } catch {
        alert('Could not join room. Please try again.');
        setDeepLinkPending(false);
      }
    };

    autoJoin();
  }, [API_URL]);

  // Handle login actions
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGuestLogin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/guest`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.user_id) {
        setIsLoggedIn(true);
        setLoggedInUser(data);
        localStorage.setItem('mimiLoggedIn', 'true');
        localStorage.setItem('mimiUser', JSON.stringify(data));
        setShowLoginScreen(false);
        setTimeout(() => proceedToMatchmakingRef.current?.(), 50);
      }
    } catch {
      // Fallback: just continue as guest locally
      setIsLoggedIn(true);
      const guestData = { name: `Guest_${Math.random().toString(36).slice(2, 6)}`, email: '' };
      setLoggedInUser(guestData);
      localStorage.setItem('mimiLoggedIn', 'true');
      localStorage.setItem('mimiUser', JSON.stringify(guestData));
      setShowLoginScreen(false);
      setTimeout(() => proceedToMatchmakingRef.current?.(), 50);
    }
  };

  // Matchmaking cities and usernames for current session
  const matchmakingCitiesRef = useRef([]);
  const matchmakingNamesRef = useRef([]);
  const [matchmakingPlayers, setMatchmakingPlayers] = useState([]);
  const matchmakingTimerRef = useRef(null);

  // Proceed to matchmaking (called after login or if already logged in)
  const proceedToMatchmakingRef = useRef(null);

  // Multiplayer ranking system
  const [playerDead, setPlayerDead] = useState(false); // player died but AI still playing
  const [showRankings, setShowRankings] = useState(false); // final rankings screen
  const [rankings, setRankings] = useState([]); // [{name, score, position, isPlayer, character}]
  const deathOrderRef = useRef([]); // tracks order of elimination

  // Character select UI step (internal state moved to CharacterSelect component)
  
  // Play with Friends — room state
  const [roomScreen, setRoomScreen] = useState(null); // null, 'create_join', 'lobby'
  const [roomCode, setRoomCode] = useState(null);
  const [roomPlayerId, setRoomPlayerId] = useState(null);
  const roomWsRef = useRef(null); // WebSocket ref for in-game sync
  const remotePlayersRef = useRef({}); // { playerId: { x, y, alive, score, coins, character, name } }
  const [remotePlayersDisplay, setRemotePlayersDisplay] = useState({});
  const allRoomDeadRef = useRef(false); // flag when all room players dead
  const isRoomGameRef = useRef(false); // true when playing in a real room (not AI fakeOnline)
  const roomSeedRNGRef = useRef(null); // seeded RNG for deterministic pipe generation in rooms
  const roomRoundRef = useRef(0); // round counter for re-seeding on Play Again
  const [roomReadyCount, setRoomReadyCount] = useState(0);
  const [roomTotalCount, setRoomTotalCount] = useState(0);
  const [gameFloatingEmojis, setGameFloatingEmojis] = useState([]); // [{id, emoji, playerId}]
  
  // AI opponents state (up to 3)
  const aiPlayersRef = useRef([]);
  const [aiPlayersDisplay, setAiPlayersDisplay] = useState([]);

  // Get the active character's GIF for gameplay
  const characterUrl = selectedCharacter ? selectedCharacter.gif : CHARACTERS[0].gif;
  
  // Intro video
  const introVideoUrl = 'https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/dcssu6al_grok_video_2026-03-31-07-11-33.mp4';
  
  // Outro video - plays before game over screen
  const outroVideoUrl = 'https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/p2gfmirc_grok_video_2026-03-30-23-11-42.mp4';
  const outroMusicUrl = 'https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/gmy6c4sm_grok_video_2026-03-30-23-11-42.mp3';
  const outroAudioRef = useRef(null);

  // Audio files
  const introAudioRef = useRef(null);
  const birdSoundsRef = useRef(null);
  const monkeySoundsRef = useRef(null);
  const bgMusicRef = useRef(null);

  // Initialize audio elements
  useEffect(() => {
    introAudioRef.current = new Audio('https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/cxk7xryt_grok_video_2026-03-28-13-55-12.mp3');
    introAudioRef.current.volume = 0.6;

    // Background music
    bgMusicRef.current = new Audio('https://customer-assets.emergentagent.com/job_mimi-ultra/artifacts/csfhtr7y_dragon-studio-atmospheric-drums-443147.mp3');
    bgMusicRef.current.loop = true;
    bgMusicRef.current.volume = 0.3;
  }, []);

  // Music control - start/stop based on game state and toggle
  useEffect(() => {
    if (!bgMusicRef.current) return;

    if (gameState === 'playing' && isMusicOn) {
      bgMusicRef.current.play().catch(e => console.log('Music blocked:', e));
    } else {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
    }
  }, [gameState, isMusicOn]);

  // Toggle music function
  const toggleMusic = useCallback(() => {
    const newState = !isMusicOn;
    setIsMusicOn(newState);
    localStorage.setItem('mimiMusicOn', newState.toString());
  }, [isMusicOn]);

  // Sound effects using Web Audio API
  const audioContextRef = useRef(null);
  
  // Initialize audio context on first interaction (mobile requirement)
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  }, []);

  // Flap/Jump sound
  const playFlapSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(300, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.1);
  }, [initAudio, sfxOn]);

  // Coin collection sound
  const playCoinSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.1);
  }, [initAudio, sfxOn]);

  // Score point sound (passing pipe)
  const playScoreSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    oscillator.frequency.setValueAtTime(900, ctx.currentTime + 0.05);
    
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);
  }, [initAudio, sfxOn]);

  // Hit/Collision sound — cartoon "bonk" with thud
  const playHitSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    // Bonk tone
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(350, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.18);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.18);
    
    // Thud noise layer
    const noiseLen = 0.08;
    const buf = ctx.createBuffer(1, ctx.sampleRate * noiseLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / buf.length);
    const noise = ctx.createBufferSource();
    const nGain = ctx.createGain();
    noise.buffer = buf;
    noise.connect(nGain);
    nGain.connect(ctx.destination);
    nGain.gain.setValueAtTime(0.15, ctx.currentTime);
    nGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + noiseLen);
    noise.start(ctx.currentTime);
  }, [initAudio, sfxOn]);

  // Balloon boost pickup — sparkly magical whoosh
  const playBoostSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    // Rising sparkle tone
    const osc1 = ctx.createOscillator();
    const g1 = ctx.createGain();
    osc1.connect(g1);
    g1.connect(ctx.destination);
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(600, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.25);
    g1.gain.setValueAtTime(0.2, ctx.currentTime);
    g1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.3);
    
    // Shimmer harmonic
    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    osc2.connect(g2);
    g2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(900, ctx.currentTime + 0.05);
    osc2.frequency.exponentialRampToValueAtTime(2400, ctx.currentTime + 0.3);
    g2.gain.setValueAtTime(0.12, ctx.currentTime + 0.05);
    g2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc2.start(ctx.currentTime + 0.05);
    osc2.stop(ctx.currentTime + 0.35);
    
    // Chime ding at end
    const osc3 = ctx.createOscillator();
    const g3 = ctx.createGain();
    osc3.connect(g3);
    g3.connect(ctx.destination);
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(1400, ctx.currentTime + 0.15);
    g3.gain.setValueAtTime(0.18, ctx.currentTime + 0.15);
    g3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
    osc3.start(ctx.currentTime + 0.15);
    osc3.stop(ctx.currentTime + 0.45);
  }, [initAudio, sfxOn]);

  // Tornado hit — swirling descending whoosh
  const playTornadoSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    // Swirling wind noise
    const bufLen = 0.4;
    const buf = ctx.createBuffer(1, ctx.sampleRate * bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + bufLen);
    filter.Q.value = 4;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + bufLen);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start(ctx.currentTime);
    
    // Descending tone
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(500, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.35);
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  }, [initAudio, sfxOn]);

  // Slow-down whoosh — airy wind sound
  const playSlowdownSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    // Filtered noise for wind whoosh
    const bufLen = 0.25;
    const buf = ctx.createBuffer(1, ctx.sampleRate * bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1200, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + bufLen);
    filter.Q.value = 2;
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + bufLen);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start(ctx.currentTime);
  }, [initAudio, sfxOn]);

  // Balloon pop — sharp pop when character dies
  const playBalloonPopSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    // Sharp pop attack
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
    
    // Pop burst noise
    const popLen = 0.06;
    const buf = ctx.createBuffer(1, ctx.sampleRate * popLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) {
      const decay = 1 - i / buf.length;
      data[i] = (Math.random() * 2 - 1) * decay * decay;
    }
    const noise = ctx.createBufferSource();
    const nGain = ctx.createGain();
    noise.buffer = buf;
    noise.connect(nGain);
    nGain.connect(ctx.destination);
    nGain.gain.setValueAtTime(0.3, ctx.currentTime);
    nGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + popLen);
    noise.start(ctx.currentTime);
    
    // Sad descending tone after pop
    const sad = ctx.createOscillator();
    const sGain = ctx.createGain();
    sad.connect(sGain);
    sGain.connect(ctx.destination);
    sad.type = 'sine';
    sad.frequency.setValueAtTime(500, ctx.currentTime + 0.12);
    sad.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.6);
    sGain.gain.setValueAtTime(0.15, ctx.currentTime + 0.12);
    sGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.65);
    sad.start(ctx.currentTime + 0.12);
    sad.stop(ctx.currentTime + 0.65);
  }, [initAudio, sfxOn]);

  // Game Over sound — muted trombone wah-wah
  const playGameOverSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    // Wah-wah descending notes
    const notes = [350, 330, 280, 200];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      const t = ctx.currentTime + i * 0.18;
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.85, t + 0.15);
      gain.gain.setValueAtTime(0.2, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.16);
      osc.start(t);
      osc.stop(t + 0.18);
    });
  }, [initAudio, sfxOn]);

  // Intro sound - play actual audio file
  const playIntroSound = useCallback(() => {
    if (introAudioRef.current) {
      introAudioRef.current.currentTime = 0;
      introAudioRef.current.play().catch(e => {
        console.log('Intro audio blocked:', e);
      });
    }
  }, []);

  // Background bird sounds generator
  const playBirdAmbience = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;

    const createBirdChirp = (delay) => {
      setTimeout(() => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        // Random bird chirp frequencies
        const baseFreq = 2000 + Math.random() * 1000;
        oscillator.frequency.setValueAtTime(baseFreq, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(baseFreq * 1.3, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(baseFreq * 0.9, ctx.currentTime + 0.15);
        
        gainNode.gain.setValueAtTime(0.08, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.2);
      }, delay);
    };

    // Create random chirps
    for (let i = 0; i < 5; i++) {
      createBirdChirp(Math.random() * 3000);
    }
  }, [initAudio, sfxOn]);

  // Background monkey sounds generator
  const playMonkeyAmbience = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;

    const createMonkeySound = (delay) => {
      setTimeout(() => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sawtooth';
        
        // Monkey "ooh ooh" sound
        const baseFreq = 400 + Math.random() * 200;
        oscillator.frequency.setValueAtTime(baseFreq, ctx.currentTime);
        oscillator.frequency.setValueAtTime(baseFreq * 1.5, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(baseFreq, ctx.currentTime + 0.2);
        
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      }, delay);
    };

    // Create random monkey calls
    for (let i = 0; i < 3; i++) {
      createMonkeySound(Math.random() * 4000);
    }
  }, [initAudio, sfxOn]);

  // Outro/Falling sound - cartoon falling/crash sound
  const playFallingSound = useCallback(() => {
    initAudio();
    const ctx = audioContextRef.current;
    if (!ctx || !sfxOn) return;
    
    console.log('Playing falling sound!');
    
    // Create descending "falling" sound
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'sawtooth';
    
    // Descending frequency (falling effect)
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.5);
    oscillator.frequency.setValueAtTime(100, ctx.currentTime + 0.5);
    
    // Volume envelope
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.3);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.8);
    
    // Add "crash" at the end
    setTimeout(() => {
      const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseBuffer.length; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const noise = ctx.createBufferSource();
      const noiseGain = ctx.createGain();
      
      noise.buffer = noiseBuffer;
      noise.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      
      noiseGain.gain.setValueAtTime(0.2, ctx.currentTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      
      noise.start();
    }, 500);
  }, [initAudio, sfxOn]);

  // Handle video end or skip
  const handleIntroEnd = useCallback(() => {
    // Stop intro music when skipping or when intro ends
    if (introAudioRef.current) {
      introAudioRef.current.pause();
      introAudioRef.current.currentTime = 0;
    }
    setGameState('splash');
    // Reset frame counter when starting fresh
    frameRef.current = 0;
      cameraXRef.current = 0;
    lastPipeFrame.current = 0;
    lastCoinFrame.current = 0;
    lastCloudFrame.current = 0;
  }, []);

  // Start the matchmaking sequence
  const proceedToMatchmaking = useCallback(() => {
    if (!selectedCharacter) return;
    // Pick 4 random cities and usernames for this session
    matchmakingCitiesRef.current = getRandomCities(4);
    const shuffledNames = [...FAKE_USERNAMES].sort(() => Math.random() - 0.5);
    matchmakingNamesRef.current = shuffledNames.slice(0, 4);
    
    // Show matchmaking screen — reveal players one by one
    setMatchmakingPlayers([{ ...selectedCharacter, isPlayer: true }]);
    setGameState('matchmaking');
    
    // Stagger revealing AI "players" over 8 seconds
    const aiPlayers = aiPlayersRef.current;
    const delays = [2500, 5000, 7500];
    aiPlayers.forEach((ai, i) => {
      setTimeout(() => {
        setMatchmakingPlayers(prev => [...prev, ai]);
      }, delays[i]);
    });
    
    // After all revealed + pause, go to start (11 seconds total)
    matchmakingTimerRef.current = setTimeout(() => {
      setGameState('start');
    }, 11000);
  }, [selectedCharacter]);

  // Keep ref in sync
  useEffect(() => {
    proceedToMatchmakingRef.current = proceedToMatchmaking;
  }, [proceedToMatchmaking]);

  // Handle character selection
  const handleCharacterSelect = useCallback((character, mode) => {
    setSelectedCharacter(character);
    setGameMode(mode);
    gameModeRef.current = mode;
    if (mode === 'fakeOnline') {
      // Initialize AI opponents
      const otherChars = CHARACTERS.filter(c => c.id !== character.id);
      const numAI = Math.min(3, otherChars.length);
      const shuffled = [...otherChars].sort(() => Math.random() - 0.5);
      const aiPlayers = shuffled.slice(0, numAI).map((c, i) => ({
        id: c.id,
        name: c.name,
        gif: c.gif,
        png: c.png,
        color: c.color,
        x: 50 + i * 15,
        y: 120 + i * 130,
        velocity: 0,
        lives: 3,
        score: 0,
        coins: 0,
        isInvincible: false,
        invincibleUntil: 0,
        skillLevel: [0.55, 0.35, 0.7][i],
        reactionDelay: [8, 18, 5][i],
        mistakeRate: [0.06, 0.15, 0.03][i],
        panicJump: [false, true, false][i],
        preferredHeight: [0.3, 0.7, 0.5][i],
        lastJumpFrame: 0,
        driftPhase: Math.random() * Math.PI * 2,
        isSliding: false,
        passedPipeIds: new Set(),
      }));
      aiPlayersRef.current = aiPlayers;
      setAiPlayersDisplay(aiPlayers.map(p => ({ ...p })));
      
      // Check if already logged in
      if (isLoggedIn) {
        // Already logged in — go straight to matchmaking
        // Need a slight delay so selectedCharacter state updates
        setTimeout(() => {
          proceedToMatchmakingRef.current?.();
        }, 50);
      } else {
        // Show login screen first
        setShowLoginScreen(true);
      }
    } else {
      aiPlayersRef.current = [];
      setAiPlayersDisplay([]);
      setGameState('start');
    }
  }, [isLoggedIn]);

  // Handle outro video end
  const handleOutroEnd = useCallback(() => {
    if (outroAudioRef.current) {
      outroAudioRef.current.pause();
      outroAudioRef.current = null;
    }
    setGameState('gameOver');
  }, []);

  // Finalize solo mode death — called after revive countdown expires or if already revived
  const finalizeSoloDeath = useCallback(() => {
    decrementAdUnlocks();
    const newDeathCount = deathCount + 1;
    setDeathCount(newDeathCount);
    localStorage.setItem('mimiDeathCount', newDeathCount.toString());
    
    setCurrentLevel(0);
    currentLevelRef.current = 0;
    if (newDeathCount % 3 === 0) {
      setShowInterstitialAd(true);
      setTimeout(() => {
        setShowInterstitialAd(false);
        setGameState(outroOn ? 'outro' : 'gameOver');
      }, 3000);
    } else {
      setGameState(outroOn ? 'outro' : 'gameOver');
    }
    
    if (scoreRef.current > highScore) {
      setHighScore(scoreRef.current);
      localStorage.setItem('mimiHighScore', scoreRef.current.toString());
    }
    fetch(`${API_URL}/api/leaderboard/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: selectedCharacter?.name || 'Unknown',
        character: selectedCharacter?.id || 'mimi',
        score: scoreRef.current, coins, level: currentLevelRef.current,
        mode: gameMode,
      }),
    }).catch(() => {});
  }, [deathCount, highScore, coins, gameMode, selectedCharacter, decrementAdUnlocks, outroOn]);

  // Handle revive — player tapped Continue during countdown
  const handleRevive = useCallback(() => {
    // Clear countdown timer
    if (reviveTimerRef.current) {
      clearInterval(reviveTimerRef.current);
      reviveTimerRef.current = null;
    }
    setShowRevivePopup(false);
    hasRevivedRef.current = true;

    // Watch ad then revive
    watchAdToUnlock('revive').then(() => {
      // Grant +1 life and resume
      livesRef.current = 1;
      setLives(1);
      // Make player invincible briefly after revive
      isInvincibleRef.current = true;
      playerVelocityRef.current = JUMP_FORCE * 0.5;
      playerYRef.current = Math.min(playerYRef.current, 400);
      setTimeout(() => { isInvincibleRef.current = false; }, 2000);
      // Unfreeze physics — game loop is still running, this resumes movement
      revivePendingRef.current = false;
    });
  }, [watchAdToUnlock, JUMP_FORCE]);

  // Handle quitting online game while spectating AI
  const handleQuitOnlineGame = useCallback(() => {
    // Player clicked Quit while watching AI — build rankings from current state
    const deathOrder = deathOrderRef.current;
    const allParticipants = [
      { name: selectedCharacter?.name || 'You', isPlayer: true, score: scoreRef.current + totalCoinsRef.current, pipes: scoreRef.current, coins: totalCoinsRef.current, character: selectedCharacter, alive: false },
      ...aiPlayersRef.current.map(ai => ({ name: ai.name, isPlayer: false, score: ai.score + (ai.coins || 0), pipes: ai.score, coins: ai.coins || 0, character: ai, alive: ai.lives > 0 })),
    ];
    // Sort: alive first, then by death order (died later = better), then by score
    allParticipants.sort((a, b) => {
      if (a.alive && !b.alive) return -1;
      if (!a.alive && b.alive) return 1;
      const aDeathIdx = deathOrder.findIndex(d => d.name === a.name && d.isPlayer === a.isPlayer);
      const bDeathIdx = deathOrder.findIndex(d => d.name === b.name && d.isPlayer === b.isPlayer);
      if (aDeathIdx !== -1 && bDeathIdx !== -1) {
        return bDeathIdx - aDeathIdx;
      }
      return b.score - a.score;
    });
    setRankings(allParticipants.map((p, i) => ({ ...p, position: i + 1 })));
    setShowRankings(true);
    setGameState('rankings');
  }, [selectedCharacter, score]);

  // Handle rankings screen "Continue" button
  const handleRankingsDone = useCallback(() => {
    // Save score to leaderboard
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/leaderboard/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: selectedCharacter?.name || 'Unknown',
        character: selectedCharacter?.id || 'mimi',
        score, coins, level: currentLevel,
        mode: 'fakeOnline',
      }),
    }).catch(() => {});

    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('mimiHighScore', score.toString());
    }

    // Reset all state
    setShowRankings(false);
    setRankings([]);
    setPlayerDead(false);
    deathOrderRef.current = [];
    scoreRef.current = 0;
    setScore(0);
    setCoins(0);
    setLives(3);
    livesRef.current = 3;
    setCurrentLevel(0);
    currentLevelRef.current = 0;
    playerYRef.current = 250;
    playerVelocityRef.current = 0;
    pipesRef.current = [];
    coinsListRef.current = [];
    cloudsRef.current = [];
    tornadoesRef.current = [];
    balloonPowerupsRef.current = [];
    setPlayerDisplay({ y: 250, rotation: 0 });
    setGameObjects({ pipes: [], coins: [], clouds: [], balloons: [], tornadoes: [], cameraX: 0 });
    frameRef.current = 0;
      cameraXRef.current = 0;
    pipeCountInLevelRef.current = 0;
      balloonsSpawnedInLevelRef.current = 0;
      tornadoesSpawnedInLevelRef.current = 0;
    levelStartScoreRef.current = 0;
    hasBalloonBoostRef.current = false;
    boostPipesPassedRef.current = 0;
    isTornadoKnockbackRef.current = false;
    knockbackFramesRef.current = 0;
    aiPlayersRef.current = [];
    setAiPlayersDisplay([]);
    isInvincibleRef.current = false;
    setGameState('characterSelect');
  }, [selectedCharacter, score, coins, currentLevel, highScore]);

  // Handle "Play Again" in room mode — notify server, wait for all players
  const handleRoomPlayAgain = useCallback(() => {
    if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN) {
      roomWsRef.current.send(JSON.stringify({ type: 'play_again' }));
    }
  }, []);

  // Handle "Home" in room mode — leave room and go to character select
  const handleRoomGoHome = useCallback(() => {
    if (roomWsRef.current) {
      roomWsRef.current.close();
      roomWsRef.current = null;
    }
    isRoomGameRef.current = false;
    roomSeedRNGRef.current = null;
    roomRoundRef.current = 0;
    setRoomReadyCount(0);
    setRoomTotalCount(0);
    setShowRankings(false);
    setRankings([]);
    setPlayerDead(false);
    deathOrderRef.current = [];
    remotePlayersRef.current = {};
    setRemotePlayersDisplay({});
    allRoomDeadRef.current = false;
    scoreRef.current = 0;
    setScore(0);
    setCoins(0);
    totalCoinsRef.current = 0;
    setLives(3);
    livesRef.current = 3;
    setCurrentLevel(0);
    currentLevelRef.current = 0;
    playerYRef.current = 250;
    playerVelocityRef.current = 0;
    pipesRef.current = [];
    coinsListRef.current = [];
    cloudsRef.current = [];
    tornadoesRef.current = [];
    balloonPowerupsRef.current = [];
    setPlayerDisplay({ y: 250, rotation: 0 });
    setGameObjects({ pipes: [], coins: [], clouds: [], balloons: [], tornadoes: [], cameraX: 0 });
    frameRef.current = 0;
    cameraXRef.current = 0;
    pipeCountInLevelRef.current = 0;
    balloonsSpawnedInLevelRef.current = 0;
    tornadoesSpawnedInLevelRef.current = 0;
    levelStartScoreRef.current = 0;
    hasBalloonBoostRef.current = false;
    boostPipesPassedRef.current = 0;
    isTornadoKnockbackRef.current = false;
    knockbackFramesRef.current = 0;
    aiPlayersRef.current = [];
    setAiPlayersDisplay([]);
    isInvincibleRef.current = false;
    setRoomScreen(null);
    setRoomCode(null);
    setRoomPlayerId(null);
    setGameState('characterSelect');
  }, []);

  // Reset game state for room restart (called when all players hit Play Again)
  const resetForRoomRestart = useCallback(() => {
    setShowRankings(false);
    setRankings([]);
    setPlayerDead(false);
    deathOrderRef.current = [];
    allRoomDeadRef.current = false;
    hasRevivedRef.current = false; // Reset revive for new round
    revivePendingRef.current = false;
    setRoomReadyCount(0);
    setRoomTotalCount(0);
    scoreRef.current = 0;
    setScore(0);
    setCoins(0);
    totalCoinsRef.current = 0;
    setLives(3);
    livesRef.current = 3;
    setCurrentLevel(0);
    currentLevelRef.current = 0;
    playerYRef.current = 250;
    playerVelocityRef.current = 0;
    playerXRef.current = 80;
    pipesRef.current = [];
    coinsListRef.current = [];
    cloudsRef.current = [];
    tornadoesRef.current = [];
    balloonPowerupsRef.current = [];
    setPlayerDisplay({ y: 250, rotation: 0 });
    setGameObjects({ pipes: [], coins: [], clouds: [], balloons: [], tornadoes: [], cameraX: 0 });
    frameRef.current = 0;
    cameraXRef.current = 0;
    pipeCountInLevelRef.current = 0;
    balloonsSpawnedInLevelRef.current = 0;
    tornadoesSpawnedInLevelRef.current = 0;
    levelStartScoreRef.current = 0;
    hasBalloonBoostRef.current = false;
    boostPipesPassedRef.current = 0;
    isTornadoKnockbackRef.current = false;
    knockbackFramesRef.current = 0;
    isInvincibleRef.current = false;
    // Reset remote player states for new round
    for (const key of Object.keys(remotePlayersRef.current)) {
      remotePlayersRef.current[key].alive = true;
      remotePlayersRef.current[key].score = 0;
      remotePlayersRef.current[key].coins = 0;
    }
    setRemotePlayersDisplay({ ...remotePlayersRef.current });
    // Re-seed RNG for fresh but synchronized pipe layout
    if (roomCode) {
      roomRoundRef.current++;
      roomSeedRNGRef.current = createSeededRNG(roomCode + '_r' + roomRoundRef.current);
    }
    // Go to 'start' screen — first tap begins the game
    setGameState('start');
  }, [roomCode]);

  // Send in-game emoji via WebSocket
  const handleSendGameEmoji = useCallback((emoji) => {
    if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN) {
      roomWsRef.current.send(JSON.stringify({ type: 'game_emoji', emoji }));
    }
  }, []);

  // Update level settings every frame (uses currentLevelRef for real-time)
  const updateLevelSettings = useCallback(() => {
    switch (currentLevelRef.current) {
      case 0: // Sky (Level 1) — Tutorial
        levelSettings.current = { gravity: 0.3, pipeSpeed: 2, pipeGap: 250, pipeSpawnRate: 168, margin: 60 };
        break;
      case 1: // Desert (Level 2)
        levelSettings.current = { gravity: 0.3, pipeSpeed: 2.8, pipeGap: 180, pipeSpawnRate: 168, margin: 60 };
        break;
      case 2: // Jungle (Level 3)
        levelSettings.current = { gravity: 0.3, pipeSpeed: 2.6, pipeGap: 210, pipeSpawnRate: 196, margin: 55 };
        break;
      case 3: // London (Level 4)
        levelSettings.current = { gravity: 0.32, pipeSpeed: 2.8, pipeGap: 200, pipeSpawnRate: 189, margin: 55 };
        break;
      case 4: // Mountain (Level 5)
        levelSettings.current = { gravity: 0.3, pipeSpeed: 2.9, pipeGap: 195, pipeSpawnRate: 182, margin: 50 };
        break;
      case 5: // Space (Level 6)
        levelSettings.current = { gravity: 0.2, pipeSpeed: 3.0, pipeGap: 190, pipeSpawnRate: 175, margin: 50 };
        break;
      case 6: // Volcano (Level 7)
        levelSettings.current = { gravity: 0.33, pipeSpeed: 3.2, pipeGap: 185, pipeSpawnRate: 168, margin: 45 };
        break;
      default:
        break;
    }
  }, []);

  // Jump handler
  const handleJump = useCallback(() => {
    if (gameState === 'intro') {
      // Skip intro
      handleIntroEnd();
    } else if (gameState === 'outro') {
      // Skip outro
      handleOutroEnd();
    } else if (gameState === 'start') {
      initAudio(); // Unlock audio on first tap
      setGameState('playing');
      playerVelocityRef.current = JUMP_FORCE;
      playFlapSound();
      // Ensure player is in safe position
      playerYRef.current = 250;
      playerXRef.current = 80;
      frameRef.current = 0;
      cameraXRef.current = 0;
      scoreRef.current = 0;
      lastPipeFrame.current = 0;
      lastCoinFrame.current = 0;
      lastCloudFrame.current = 0;
      lastBalloonFrame.current = 0;
      lastTornadoFrame.current = 0;
      pipeCountInLevelRef.current = 0;
      balloonsSpawnedInLevelRef.current = 0;
      tornadoesSpawnedInLevelRef.current = 0;
      levelStartScoreRef.current = 0;
      pipesRef.current = [];
      coinsListRef.current = [];
      cloudsRef.current = [];
      balloonPowerupsRef.current = [];
      tornadoesRef.current = [];
      setLives(3);
      livesRef.current = 3;
      isInvincibleRef.current = false;
      totalCoinsRef.current = 0;
      lastHealAtRef.current = 0;
      hasBalloonBoostRef.current = false;
      boostPipesPassedRef.current = 0;
      isTornadoKnockbackRef.current = false;
      knockbackFramesRef.current = 0;
      setBalloonBoostActive(false);
      setPlayerDead(false);
      deathOrderRef.current = [];
      hasRevivedRef.current = false; // Reset revive for new run
      revivePendingRef.current = false;
      if (balloonBoostTimerRef.current) clearTimeout(balloonBoostTimerRef.current);
    } else if (gameState === 'playing') {
      playerVelocityRef.current = JUMP_FORCE;
      playFlapSound();
    } else if (gameState === 'gameOver') {
      // Reset game - go to character select, RESET TO LEVEL 1
      setGameState('characterSelect');
      scoreRef.current = 0;
      setScore(0);
      setCoins(0);
      setLives(3);
      livesRef.current = 3;
      setCurrentLevel(0); // Always restart from Level 1
      currentLevelRef.current = 0;
      playerYRef.current = 250;
      playerXRef.current = 80;
      playerVelocityRef.current = 0;
      playerRotationRef.current = 0;
      pipesRef.current = [];
      coinsListRef.current = [];
      cloudsRef.current = [];
      setPlayerDisplay({ y: 250, rotation: 0 });
      setGameObjects({ pipes: [], coins: [], clouds: [], balloons: [], tornadoes: [], cameraX: 0 });
      frameRef.current = 0;
      cameraXRef.current = 0;
      // Reset multiplayer state
      setPlayerDead(false);
      setShowRankings(false);
      setRankings([]);
      deathOrderRef.current = [];
      lastPipeFrame.current = 0;
      lastCoinFrame.current = 0;
      lastCloudFrame.current = 0;
      lastTornadoFrame.current = 0;
      pipeCountInLevelRef.current = 0;
      balloonsSpawnedInLevelRef.current = 0;
      tornadoesSpawnedInLevelRef.current = 0;
      levelStartScoreRef.current = 0;
      isInvincibleRef.current = false;
      totalCoinsRef.current = 0;
      lastHealAtRef.current = 0;
      hasBalloonBoostRef.current = false;
      boostPipesPassedRef.current = 0;
      isTornadoKnockbackRef.current = false;
      knockbackFramesRef.current = 0;
      setBalloonBoostActive(false);
      balloonPowerupsRef.current = [];
      tornadoesRef.current = [];
      if (balloonBoostTimerRef.current) clearTimeout(balloonBoostTimerRef.current);
    }
  }, [gameState, JUMP_FORCE, handleIntroEnd, handleOutroEnd, initAudio, playFlapSound]);

  // Collision detection
  const checkCollision = useCallback((pY, pipeList, pX = 80) => {
    // Add generous padding to make collision very forgiving (smaller hitbox than visual size)
    const hitboxPadding = 25; // Reduce hitbox by 25px on each side for easier gameplay
    const playerLeft = pX + hitboxPadding;
    const playerRight = pX + PLAYER_SIZE - hitboxPadding;
    const playerTop = pY + hitboxPadding;
    const playerBottom = pY + PLAYER_SIZE - hitboxPadding;

    // Check ground and ceiling collision
    if (playerBottom >= GAME_HEIGHT || playerTop <= 0) {
      return true;
    }

    // Check pipe collision
    for (let pipe of pipeList) {
      if (
        playerRight > pipe.x &&
        playerLeft < pipe.x + PIPE_WIDTH
      ) {
        if (playerTop < pipe.topHeight || playerBottom > GAME_HEIGHT - pipe.bottomHeight) {
          return true;
        }
      }
    }

    return false;
  }, [GAME_HEIGHT, PLAYER_SIZE, PIPE_WIDTH]);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const gameLoop = () => {
      // If revive popup is showing, freeze physics but keep loop alive for rendering
      if (revivePendingRef.current) {
        // Keep remote player sync running in multiplayer
        if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN && frameRef.current % 3 === 0) {
          setRemotePlayersDisplay({ ...remotePlayersRef.current });
        }
        frameRef.current++;
        animationRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      // Update level settings every frame (uses currentLevelRef for real-time)
      updateLevelSettings();
      frameRef.current++;

      // Per-player horizontal dodge: slide player X left when slowing, slide back when not
      // Enforce 2-second max slow-down duration
      if (isSlowingRef.current && Date.now() - slowStartTimeRef.current > MAX_SLOW_DURATION) {
        isSlowingRef.current = false;
        setIsSlowing(false);
      }
      
      const effectiveSliding = isSlowingRef.current;
      const diving = isDivingRef.current;
      const pDead = livesRef.current <= 0 && gameModeRef.current === 'fakeOnline';
      
      if (pDead) {
        // Player dead in online mode — skip player physics, just update AI
      } else if (isTornadoKnockbackRef.current) {
        // TORNADO KNOCKBACK — player physically moves backward through pipe field
        knockbackFramesRef.current++;
        playerVelocityRef.current = -1; // Gentle downward drift
        playerYRef.current += playerVelocityRef.current;
        // Rush backward for 30 frames
        if (knockbackFramesRef.current <= 30) {
          playerXRef.current -= 8;
        }
        // End knockback after 30 frames — world shift to preserve position
        if (knockbackFramesRef.current > 30) {
          isTornadoKnockbackRef.current = false;
          knockbackFramesRef.current = 0;
          // WORLD SHIFT: move all objects so player returns to X=80
          // but relative positions preserved (player keeps the disadvantage)
          const shift = playerXRef.current - 80;
          pipesRef.current.forEach(p => { p.x -= shift; });
          coinsListRef.current.forEach(c => { c.x -= shift; });
          cloudsRef.current.forEach(c => { c.x -= shift; });
          balloonPowerupsRef.current.forEach(b => { b.x -= shift; });
          tornadoesRef.current.forEach(t => { t.x -= shift; });
          // Shift AI positions too — they stay ahead relative to player
          aiPlayersRef.current.forEach(ai => { ai.x = (ai.x || 80) - shift; });
          playerXRef.current = 80;
          cameraXRef.current = 0;
        }
      } else if (hasBalloonBoostRef.current) {
        // BALLOON BOOST — player rushes forward through pipe field, invincible
        // Camera follows — no screen bound limit
        playerVelocityRef.current = 0; // Freeze vertical — fly straight
        playerYRef.current = Math.max(80, Math.min(playerYRef.current, GAME_HEIGHT - 120));
        // Rush forward rapidly — camera will follow
        playerXRef.current += 14;
      } else if (effectiveSliding) {
        // FREEZE vertical position — no gravity, no falling. Just float in place.
        playerVelocityRef.current = 0;
        // Slide character backward
        playerXRef.current = Math.max(20, playerXRef.current - 3);
      } else if (diving) {
        // POWER DIVE — instant high-velocity drop, overrides all upward momentum
        playerVelocityRef.current = 12; // Immediate fast descent
        playerYRef.current += playerVelocityRef.current;
      } else {
        // Normal physics
        let grav = levelSettings.current.gravity;
        
        // Level-specific physics
        const lvl = levels[currentLevelRef.current];
        if (lvl === 'mountain' && !effectiveSliding) {
          // Wind gusts — random horizontal force + vertical turbulence
          const windGust = Math.sin(frameRef.current * 0.05) * 0.15;
          grav += windGust;
        } else if (lvl === 'space') {
          // Zero-G zones — gravity reverses in patches based on player Y
          const inZeroG = Math.sin(frameRef.current * 0.02 + playerYRef.current * 0.01) > 0.6;
          if (inZeroG) grav *= -0.5;
        } else if (lvl === 'volcano') {
          // Heavier gravity, lava pressure
          grav *= 1.1;
        }
        
        playerVelocityRef.current += grav;
        playerYRef.current += playerVelocityRef.current;
      }
      playerRotationRef.current = effectiveSliding ? 0 : diving ? 15 : playerVelocityRef.current * 3;
      const playerX = playerXRef.current;

      // Camera system:
      // - Normal play: camera follows the player
      // - Player dead (spectating): camera follows the leading AI (furthest ahead in world)
      let cameraTarget = playerXRef.current;
      if (pDead) {
        const aliveAIs = aiPlayersRef.current.filter(ai => ai.lives > 0);
        if (aliveAIs.length > 0) {
          const leadingAI = aliveAIs.reduce((best, ai) => (ai.x || 80) > (best.x || 80) ? ai : best, aliveAIs[0]);
          cameraTarget = leadingAI.x || 80;
        }
      }
      const targetCameraX = cameraTarget - 80;
      cameraXRef.current += (targetCameraX - cameraXRef.current) * 0.12;

      // Update display state every frame for smooth animation
      setPlayerDisplay({
        y: playerYRef.current,
        rotation: playerRotationRef.current
      });

      // Check for level progression — based on pipe count (50 pipes per level)
      const PIPES_PER_LEVEL = 40;
      const cl = currentLevelRef.current;
      const pipesInLvl = pipeCountInLevelRef.current;
      if (pipesInLvl >= PIPES_PER_LEVEL && cl < 6) {
        const nextLevel = cl + 1;
        // In solo mode, levels 2+ require Pro/ad unlock (except level 1 which is always free)
        if (nextLevel >= 2 && gameModeRef.current !== 'fakeOnline' && maxLevelRef.current < nextLevel) {
          // Show ad gate — only in solo
          setShowLevelAdPrompt(true);
          setGameState('paused');
        } else {
          currentLevelRef.current = nextLevel;
          setCurrentLevel(nextLevel);
          pipeCountInLevelRef.current = 0;
          balloonsSpawnedInLevelRef.current = 0;
          tornadoesSpawnedInLevelRef.current = 0;
          setShowLevelUp(true);
          setTimeout(() => setShowLevelUp(false), 3000);
        }
      }

      // Pipe speed is ALWAYS constant — never modified by boost/knockback
      // Boost/knockback works by moving the PLAYER position, not pipe speed
      const currentSpeed = levelSettings.current.pipeSpeed;

      // Spawn pipes — Level 0-1: 30% more pipes. Level 2+: use level spawn rate as-is (already generous)
      const adjustedSpawnRate = currentLevelRef.current <= 1
        ? Math.floor(levelSettings.current.pipeSpawnRate * 0.7)
        : levelSettings.current.pipeSpawnRate;
      if (frameRef.current - lastPipeFrame.current > adjustedSpawnRate) {
        // Use seeded RNG in room mode so all players see identical pipes
        const rng = roomSeedRNGRef.current || Math.random;
        const margin = levelSettings.current.margin;
        const gap = levelSettings.current.pipeGap;
        const minTop = margin;
        const maxTop = GAME_HEIGHT - gap - margin;
        const topHeight = rng() * (maxTop - minTop) + minTop;
        const bottomHeight = GAME_HEIGHT - topHeight - gap;
        
        // Mark if this is the "moving" pipe (oscillates up/down)
        const isMoving = rng() < 0.2; // 20% chance to be a moving pipe
        
        // Track pipe count within the current level for opening/closing placement
        pipeCountInLevelRef.current++;
        
        // Opening/closing pipes: placed at the END of each level
        // Level 1: 0, Level 2: 1, Level 3: 2, Level 4: 3, Level 5: 4, Level 6: 5, Level 7: 6
        const lvlIdx = currentLevelRef.current;
        const openClosePipeCount = Math.max(0, lvlIdx);
        const pipeInLevel = pipeCountInLevelRef.current;
        const startOCAt = 40 - openClosePipeCount; // 40 pipes per level
        let isOpenClose = false;
        let openCloseSpeed = 0;
        if (lvlIdx >= 1 && pipeInLevel > startOCAt) {
          isOpenClose = true;
          // Speed gets faster in later levels
          openCloseSpeed = 0.04 + (lvlIdx * 0.01) + rng() * 0.02;
        }
        
        // Spawn position: always at the right edge of the camera view
        const spawnX = GAME_WIDTH + Math.max(0, cameraXRef.current);
        pipesRef.current.push({
          id: Date.now(),
          x: spawnX,
          topHeight,
          bottomHeight,
          originalTopHeight: topHeight,
          originalGap: gap,
          passed: false,
          isMoving,
          isOpenClose,
          openCloseSpeed,
          movePhase: rng() * Math.PI * 2,
        });
        lastPipeFrame.current = frameRef.current;
      }

      // Spawn coins
      if (frameRef.current - lastCoinFrame.current > 100) {
        const rng = roomSeedRNGRef.current || Math.random;
        const coinSpawnX = GAME_WIDTH + Math.max(0, cameraXRef.current);
        coinsListRef.current.push({
          id: Date.now(),
          x: coinSpawnX,
          y: rng() * (GAME_HEIGHT - 200) + 100,
        });
        lastCoinFrame.current = frameRef.current;
      }

      // Spawn clouds - spawn some immediately
      if (frameRef.current === 1 || frameRef.current - lastCloudFrame.current > 120) {
        const rng = roomSeedRNGRef.current || Math.random;
        cloudsRef.current.push({
          id: Date.now(),
          x: GAME_WIDTH + Math.max(0, cameraXRef.current),
          y: rng() * 300,
          size: rng() * 40 + 40,
        });
        lastCloudFrame.current = frameRef.current;
      }

      // Spawn balloon power-ups — 3 per level, evenly spaced across 40 pipes
      // Spawn at pipe 8, 20, 32
      const pipesInLevel = pipeCountInLevelRef.current;
      const balloonSpawnAt = [8, 20, 32];
      if (balloonsSpawnedInLevelRef.current < 3 && balloonSpawnAt.includes(pipesInLevel) && frameRef.current - lastBalloonFrame.current > 60) {
        const rng = roomSeedRNGRef.current || Math.random;
        balloonPowerupsRef.current.push({
          id: Date.now(),
          x: GAME_WIDTH + Math.max(0, cameraXRef.current),
          y: rng() * (GAME_HEIGHT - 250) + 80,
        });
        balloonsSpawnedInLevelRef.current++;
        lastBalloonFrame.current = frameRef.current;
      }

      // Spawn tornadoes — 3 per level, evenly spaced across 40 pipes
      // Spawn at pipe 12, 24, 36
      const tornadoSpawnAt = [12, 24, 36];
      if (tornadoesSpawnedInLevelRef.current < 3 && tornadoSpawnAt.includes(pipesInLevel) && frameRef.current - lastTornadoFrame.current > 60) {
        const rng = roomSeedRNGRef.current || Math.random;
        tornadoesRef.current.push({
          id: Date.now(),
          x: GAME_WIDTH + Math.max(0, cameraXRef.current),
          y: rng() * (GAME_HEIGHT - 250) + 80,
        });
        tornadoesSpawnedInLevelRef.current++;
        lastTornadoFrame.current = frameRef.current;
      }

      // Update pipes with speed modifier + moving pipe + opening/closing logic
      pipesRef.current = pipesRef.current.map((pipe) => {
        const newX = pipe.x - currentSpeed;
        let newTopHeight = pipe.topHeight;
        let newBottomHeight = pipe.bottomHeight;
        const baseGap = pipe.originalGap || levelSettings.current.pipeGap;
        
        // Moving pipe oscillation (whole pipe shifts up/down)
        if (pipe.isMoving) {
          const oscillation = Math.sin(frameRef.current * 0.03 + pipe.movePhase) * 40;
          newTopHeight = Math.max(40, Math.min(GAME_HEIGHT - baseGap - 40, pipe.originalTopHeight + oscillation));
          newBottomHeight = GAME_HEIGHT - newTopHeight - baseGap;
        }
        
        // Opening/closing pipe (gap widens and narrows dynamically)
        if (pipe.isOpenClose) {
          const gapCenter = pipe.originalTopHeight + baseGap / 2;
          // Gap oscillates ±35% of base gap
          const gapOsc = Math.sin(frameRef.current * pipe.openCloseSpeed + pipe.movePhase) * (baseGap * 0.35);
          const currentGap = baseGap + gapOsc;
          newTopHeight = Math.max(20, gapCenter - currentGap / 2);
          newBottomHeight = Math.max(20, GAME_HEIGHT - gapCenter - currentGap / 2);
        }
        
        // Check if player passed pipe (only score if player is alive)
        if (!pipe.passed && newX + PIPE_WIDTH < playerX && livesRef.current > 0) {
          scoreRef.current++;
          setScore(scoreRef.current + totalCoinsRef.current);
          playScoreSound();
          
          // Track boost pipes — end boost after 2 pipes
          if (hasBalloonBoostRef.current) {
            boostPipesPassedRef.current++;
            if (boostPipesPassedRef.current >= 2) {
              hasBalloonBoostRef.current = false;
              boostPipesPassedRef.current = 0;
              isInvincibleRef.current = false;
              setBalloonBoostActive(false);
              // WORLD SHIFT: move all objects so player returns to X=80
              // but relative positions are preserved (player keeps the advantage)
              const shift = playerXRef.current - 80;
              pipesRef.current.forEach(p => { p.x -= shift; });
              coinsListRef.current.forEach(c => { c.x -= shift; });
              cloudsRef.current.forEach(c => { c.x -= shift; });
              balloonPowerupsRef.current.forEach(b => { b.x -= shift; });
              tornadoesRef.current.forEach(t => { t.x -= shift; });
              // Shift AI positions too — they stay behind/ahead relative to player
              aiPlayersRef.current.forEach(ai => { ai.x = (ai.x || 80) - shift; });
              playerXRef.current = 80;
              cameraXRef.current = 0;
            }
          }
          
          return { ...pipe, x: newX, topHeight: newTopHeight, bottomHeight: newBottomHeight, passed: true };
        }
        return { ...pipe, x: newX, topHeight: newTopHeight, bottomHeight: newBottomHeight };
      }).filter((pipe) => pipe.x - cameraXRef.current > -PIPE_WIDTH - 50);

      // Update coins with speed modifier
      coinsListRef.current = coinsListRef.current.map((coin) => {
        return { ...coin, x: coin.x - currentSpeed };
      }).filter((coin) => coin.x - cameraXRef.current > -COIN_SIZE - 50);

      // Update balloon power-ups
      balloonPowerupsRef.current = balloonPowerupsRef.current.map((b) => {
        return { ...b, x: b.x - currentSpeed };
      }).filter((b) => b.x - cameraXRef.current > -80);

      // Update tornadoes
      tornadoesRef.current = tornadoesRef.current.map((t) => {
        // Tornadoes bob up and down slightly for visual effect
        const bob = Math.sin(frameRef.current * 0.05 + (t.id % 100)) * 1.5;
        return { ...t, x: t.x - currentSpeed, y: t.y + bob };
      }).filter((t) => t.x - cameraXRef.current > -80);

      // Update clouds
      cloudsRef.current = cloudsRef.current.map((cloud) => {
        return { ...cloud, x: cloud.x - 1 };
      }).filter((cloud) => cloud.x - cameraXRef.current > -150);

      // Check coin collection
      const playerCenterX = playerX + PLAYER_SIZE / 2;
      const playerCenterY = playerYRef.current + PLAYER_SIZE / 2;

      coinsListRef.current = coinsListRef.current.filter((coin) => {
        const coinCenterX = coin.x + COIN_SIZE / 2;
        const coinCenterY = coin.y + COIN_SIZE / 2;
        const distance = Math.sqrt(
          Math.pow(playerCenterX - coinCenterX, 2) +
            Math.pow(playerCenterY - coinCenterY, 2)
        );

        if (distance < 40) {
          setCoins((c) => {
            const newCoins = c + 1;
            totalCoinsRef.current = newCoins;
            // Update displayed score (pipes + coins)
            setScore(scoreRef.current + newCoins);
            // Heal 1 life for every 10 coins (check threshold crossing), max 5 lives
            const healThreshold = Math.floor(newCoins / 10);
            if (healThreshold > lastHealAtRef.current) {
              lastHealAtRef.current = healThreshold;
              livesRef.current = Math.min(livesRef.current + 1, 5);
              setLives(livesRef.current);
            }
            return newCoins;
          });
          playCoinSound();
          return false; // Remove coin
        }
        return true;
      });

      // Check balloon power-up collection
      balloonPowerupsRef.current = balloonPowerupsRef.current.filter((balloon) => {
        const bx = balloon.x + 20;
        const by = balloon.y + 20;
        const distance = Math.sqrt(
          Math.pow(playerCenterX - bx, 2) + Math.pow(playerCenterY - by, 2)
        );
        if (distance < 50 && !hasBalloonBoostRef.current) {
          // Activate balloon forward rush — invincible, rushing past 4 pipes
          hasBalloonBoostRef.current = true;
          boostPipesPassedRef.current = 0;
          isInvincibleRef.current = true;
          setBalloonBoostActive(true);
          playBoostSound();
          return false;
        }
        return true;
      });

      // Check tornado collision
      if (!isTornadoKnockbackRef.current && livesRef.current > 0) {
        tornadoesRef.current = tornadoesRef.current.filter((tornado) => {
          const tx = tornado.x + 20;
          const ty = tornado.y + 20;
          const distance = Math.sqrt(
            Math.pow(playerCenterX - tx, 2) + Math.pow(playerCenterY - ty, 2)
          );
          if (distance < 45) {
            // Tornado hit — push backward, no life lost
            isTornadoKnockbackRef.current = true;
            knockbackFramesRef.current = 0;
            playTornadoSound();
            return false;
          }
          return true;
        });
      }

      // === AI OPPONENTS UPDATE ===
      if (aiPlayersRef.current.length > 0) {
        const pipes = pipesRef.current;
        
        aiPlayersRef.current = aiPlayersRef.current.map((ai, idx) => {
          if (ai.lives <= 0) return ai; // Dead AI doesn't move
          
          let newVel = ai.velocity;
          let newY = ai.y;
          // AI uses world coordinates — stays near player's X position
          // (pipes scroll left, AI doesn't advance — same as player)
          let aiX = ai.x || (playerXRef.current - 20 + idx * 15);
          let isSliding = ai.isSliding || false;
          
          // AI slide-left decision: sometimes slide to dodge (freezes Y like player)
          const nearPipe = pipes.find(p => p.x > aiX && p.x < aiX + 80);
          if (nearPipe && !isSliding) {
            const gapCenter = nearPipe.topHeight + (GAME_HEIGHT - nearPipe.topHeight - nearPipe.bottomHeight) / 2;
            const aiCenter = newY + PLAYER_SIZE / 2;
            const misaligned = Math.abs(aiCenter - gapCenter) > 50;
            // Slide decision based on skill — bad players slide more desperately
            if (misaligned && Math.random() < (1 - ai.skillLevel) * 0.3) {
              isSliding = true;
            }
          }
          // Stop sliding randomly or when pipe passes
          if (isSliding && (!nearPipe || Math.random() < 0.08)) {
            isSliding = false;
          }
          
          if (isSliding) {
            // Freeze Y, slide X backward (same as player mechanic)
            newVel = 0;
            aiX -= 2;
          } else {
            // Normal gravity
            newVel += levelSettings.current.gravity;
            newY += newVel;
            // AI X position is fixed — only boost/knockback/world-shift changes it
          }
          
          // AI jump decision — world coordinates
          const nextPipe = pipes.find(p => p.x + PIPE_WIDTH > aiX && !p.passed);
          
          if (nextPipe && frameRef.current - ai.lastJumpFrame > ai.reactionDelay) {
            const gapCenter = nextPipe.topHeight + (GAME_HEIGHT - nextPipe.topHeight - nextPipe.bottomHeight) / 2;
            const aiCenter = newY + PLAYER_SIZE / 2;
            
            // MISTAKE: Sometimes the AI misjudges or doesn't react
            if (Math.random() < ai.mistakeRate) {
              // Do nothing — missed the timing (makes AI hit pipes)
            } else if (aiCenter > gapCenter - 5 + (Math.random() - 0.5) * 40) {
              newVel = JUMP_FORCE * (0.8 + Math.random() * 0.25);
              ai.lastJumpFrame = frameRef.current;
            }
            
            // Panic jump: bad players sometimes jump at the wrong time
            if (ai.panicJump && Math.random() < 0.02) {
              newVel = JUMP_FORCE * (0.6 + Math.random() * 0.5);
              ai.lastJumpFrame = frameRef.current;
            }
          } else if (!nextPipe) {
            // No pipe — drift toward preferred height with unique wave pattern
            const targetY = GAME_HEIGHT * ai.preferredHeight;
            const drift = Math.sin(frameRef.current * 0.015 + ai.driftPhase) * 30;
            if (newY > targetY + drift + 20) {
              newVel = JUMP_FORCE * (0.5 + Math.random() * 0.3);
              ai.lastJumpFrame = frameRef.current;
            }
          }
          
          // Clamp Y
          newY = Math.max(5, Math.min(GAME_HEIGHT - PLAYER_SIZE - 5, newY));
          
          // Check AI collision with pipes
          let newLives = ai.lives;
          let isInv = ai.isInvincible;
          
          if (frameRef.current > 40) {
            const aiLeft = aiX + 25;
            const aiRight = aiX + PLAYER_SIZE - 25;
            const aiTop = newY + 25;
            const aiBottom = newY + PLAYER_SIZE - 25;
            
            let hit = aiBottom >= GAME_HEIGHT || aiTop <= 0;
            if (!hit) {
              for (const pipe of pipes) {
                if (aiRight > pipe.x && aiLeft < pipe.x + PIPE_WIDTH) {
                  if (aiTop < pipe.topHeight || aiBottom > GAME_HEIGHT - pipe.bottomHeight) {
                    hit = true;
                    break;
                  }
                }
              }
            }
            
            if (hit && !isInv) {
              newLives = Math.max(0, newLives - 1);
              isInv = true;
              newVel = JUMP_FORCE * 0.8;
              newY = Math.max(newY - 30, 50);
              aiX = Math.max(15, aiX - 15);
              ai.invincibleUntil = frameRef.current + 120;
            } else if (hit && isInv) {
              newVel = JUMP_FORCE * 0.6;
              newY = Math.max(newY - 20, 50);
            }
          }
          
          // Check if invincibility expired
          if (isInv && frameRef.current > ai.invincibleUntil) {
            isInv = false;
          }
          
          // AI balloon interaction — boost forward
          if (!ai.isBoosting) {
            balloonPowerupsRef.current = balloonPowerupsRef.current.filter((balloon) => {
              const bx = balloon.x + 20;
              const by = balloon.y + 20;
              const dist = Math.sqrt(Math.pow(aiX + 25 - bx, 2) + Math.pow(newY + 25 - by, 2));
              if (dist < 50 && Math.random() < ai.skillLevel) {
                ai.isBoosting = true;
                ai.boostFrames = 0;
                return false; // Remove balloon
              }
              return true;
            });
          }
          if (ai.isBoosting) {
            ai.boostFrames = (ai.boostFrames || 0) + 1;
            aiX += 10; // Rush forward — no screen limit, camera handles visibility
            isInv = true;
            if (ai.boostFrames > 40) {
              ai.isBoosting = false;
              ai.boostFrames = 0;
            }
          }
          
          // AI tornado interaction — push backward
          if (!ai.isKnockback) {
            tornadoesRef.current = tornadoesRef.current.filter((tornado) => {
              const tx = tornado.x + 20;
              const ty = tornado.y + 20;
              const dist = Math.sqrt(Math.pow(aiX + 25 - tx, 2) + Math.pow(newY + 25 - ty, 2));
              if (dist < 45 && Math.random() < 0.7) {
                ai.isKnockback = true;
                ai.knockbackFrames = 0;
                return false;
              }
              return true;
            });
          }
          if (ai.isKnockback) {
            ai.knockbackFrames = (ai.knockbackFrames || 0) + 1;
            aiX -= 7; // Push backward — no screen limit
            if (ai.knockbackFrames > 40) {
              ai.isKnockback = false;
              ai.knockbackFrames = 0;
            }
          }
          
          // AI position stays — no drift back (world shift handles relative positions)
          
          // AI coin collection — world coordinates
          const aiCenterX = aiX + 25;
          const aiCenterY = newY + 25;
          let aiCoins = ai.coins || 0;
          coinsListRef.current = coinsListRef.current.filter((coin) => {
            const cx = coin.x + 10;
            const cy = coin.y + 10;
            const dist = Math.sqrt(Math.pow(aiCenterX - cx, 2) + Math.pow(aiCenterY - cy, 2));
            if (dist < 45 && Math.random() < ai.skillLevel * 0.6) {
              // AI collected coin
              aiCoins++;
              return false;
            }
            return true;
          });
          
          // AI score — track actual pipes passed (world coordinates)
          let newScore = ai.score;
          const aiPassedPipes = ai.passedPipeIds || new Set();
          pipesRef.current.forEach(pipe => {
            if (pipe.x + PIPE_WIDTH < aiX && !aiPassedPipes.has(pipe.id)) {
              aiPassedPipes.add(pipe.id);
              newScore++;
            }
          });
          
          return {
            ...ai,
            x: aiX,
            y: newY,
            velocity: newVel,
            lives: newLives,
            isInvincible: isInv,
            isSliding,
            score: Math.max(0, newScore),
            coins: aiCoins,
            passedPipeIds: aiPassedPipes,
          };
        });
        
        setAiPlayersDisplay(aiPlayersRef.current.map(p => ({ ...p })));
        
        // Track AI deaths for rankings
        aiPlayersRef.current.forEach(ai => {
          if (ai.lives <= 0 && !deathOrderRef.current.find(d => d.name === ai.name && !d.isPlayer)) {
            deathOrderRef.current.push({ name: ai.name, isPlayer: false, score: ai.score, character: ai });
          }
        });
        
        // Check if all AI are dead (player won) or all AI dead + player dead (game fully over)
        const aliveAI = aiPlayersRef.current.filter(ai => ai.lives > 0).length;
        const playerAlive = livesRef.current > 0;
        
        if (aliveAI === 0 && gameModeRef.current === 'fakeOnline' && !roomWsRef.current) {
          // Everyone is dead or player is last standing — show rankings
          // Use death order: whoever survived longest ranks highest
          const deathOrder = deathOrderRef.current; // earliest death first
          const allParticipants = [
            { name: selectedCharacter?.name || 'You', isPlayer: true, score: scoreRef.current + totalCoinsRef.current, pipes: scoreRef.current, coins: totalCoinsRef.current, character: selectedCharacter, alive: playerAlive },
            ...aiPlayersRef.current.map(ai => ({ name: ai.name, isPlayer: false, score: ai.score + (ai.coins || 0), pipes: ai.score, coins: ai.coins || 0, character: ai, alive: ai.lives > 0 })),
          ];
          // Sort: alive first, then by death order (died later = ranked higher), then by score
          allParticipants.sort((a, b) => {
            if (a.alive && !b.alive) return -1;
            if (!a.alive && b.alive) return 1;
            const aDeathIdx = deathOrder.findIndex(d => d.name === a.name && d.isPlayer === a.isPlayer);
            const bDeathIdx = deathOrder.findIndex(d => d.name === b.name && d.isPlayer === b.isPlayer);
            if (aDeathIdx !== -1 && bDeathIdx !== -1) {
              return bDeathIdx - aDeathIdx;
            }
            return b.score - a.score;
          });
          setRankings(allParticipants.map((p, i) => ({ ...p, position: i + 1 })));
          setShowRankings(true);
          setGameState('rankings');
          return;
        }
      }

      // Update display state for game objects every frame
      setGameObjects({
        pipes: [...pipesRef.current],
        coins: [...coinsListRef.current],
        clouds: [...cloudsRef.current],
        balloons: [...balloonPowerupsRef.current],
        tornadoes: [...tornadoesRef.current],
        cameraX: cameraXRef.current,
      });

      // Room multiplayer sync — send position every 3 frames
      if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN && frameRef.current % 3 === 0) {
        roomWsRef.current.send(JSON.stringify({
          type: 'game_update',
          state: {
            x: playerXRef.current,
            y: playerYRef.current,
            alive: livesRef.current > 0,
            score: scoreRef.current,
            coins: totalCoinsRef.current,
            character: selectedCharacter?.id || 'mimi',
            name: selectedCharacter?.name || 'Player',
            rotation: playerRotationRef.current,
          },
        }));
        // Update remote players display for rendering
        setRemotePlayersDisplay({ ...remotePlayersRef.current });
      }
      // Keepalive ping every 25s during gameplay (prevents timeout when player is dead/watching)
      if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN && frameRef.current % 1500 === 0) {
        roomWsRef.current.send(JSON.stringify({ type: 'ping' }));
      }

      // Check if all room players (local + remote) are dead
      if (roomWsRef.current && livesRef.current <= 0 && !allRoomDeadRef.current) {
          const remotes = Object.values(remotePlayersRef.current);
          const allRemoteDead = remotes.length > 0 && remotes.every(rp => !rp.alive);
          if (allRemoteDead) {
            allRoomDeadRef.current = true;
            // Build rankings from room players
            const localPlayer = { name: selectedCharacter?.name || 'You', isPlayer: true, score: scoreRef.current + totalCoinsRef.current, pipes: scoreRef.current, coins: totalCoinsRef.current, character: selectedCharacter, alive: false };
            const remotePlayers = remotes.map(rp => ({
              name: rp.name || 'Opponent', isPlayer: false,
              score: (rp.score || 0) + (rp.coins || 0), pipes: rp.score || 0, coins: rp.coins || 0,
              character: { id: rp.character, name: rp.name, color: '#ffffff' }, alive: false,
            }));
            const allPlayers = [localPlayer, ...remotePlayers];
            allPlayers.sort((a, b) => b.score - a.score);
            setRankings(allPlayers.map((p, i) => ({ ...p, position: i + 1 })));
            setShowRankings(true);
            setGameState('rankings');
            // Keep WS open for Play Again flow in room mode
            return;
          }
      }

      // Check collision (skip first 40 frames, skip if boosting or player already dead in online mode)
      if (frameRef.current > 40 && !hasBalloonBoostRef.current && !isTornadoKnockbackRef.current && livesRef.current > 0 && checkCollision(playerYRef.current, pipesRef.current, playerX)) {
        
        if (!isInvincibleRef.current) {
          // NOT invincible — take damage
          playHitSound();
          
          const newLives = livesRef.current - 1;
          livesRef.current = newLives;
          setLives(newLives);
          
          if (newLives <= 0) {
            // All lives lost — balloon pop + game over
            playBalloonPopSound();
            setTimeout(() => playGameOverSound(), 400);
            
            // Decrement ad-unlocked game count
            decrementAdUnlocks();
            
            if (gameModeRef.current === 'fakeOnline') {
              // Online/Room mode: check if revive is available
              if (!hasRevivedRef.current && !revivePendingRef.current) {
                // Show revive popup — freeze player but keep game loop running for remote sync
                revivePendingRef.current = true;
                setShowRevivePopup(true);
                setReviveCountdown(5);
                let count = 5;
                reviveTimerRef.current = setInterval(() => {
                  count--;
                  setReviveCountdown(count);
                  if (count <= 0) {
                    clearInterval(reviveTimerRef.current);
                    reviveTimerRef.current = null;
                    revivePendingRef.current = false;
                    setShowRevivePopup(false);
                    // No revive — proceed with death
                    deathOrderRef.current.push({ name: selectedCharacter?.name || 'You', isPlayer: true, score: scoreRef.current + totalCoinsRef.current, pipes: scoreRef.current, coins: totalCoinsRef.current, character: selectedCharacter });
                    setPlayerDead(true);
                    if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN) {
                      roomWsRef.current.send(JSON.stringify({ type: 'game_over', score: scoreRef.current + totalCoinsRef.current }));
                    }
                  }
                }, 1000);
              } else if (hasRevivedRef.current) {
                // Already revived once — proceed with death
                deathOrderRef.current.push({ name: selectedCharacter?.name || 'You', isPlayer: true, score: scoreRef.current + totalCoinsRef.current, pipes: scoreRef.current, coins: totalCoinsRef.current, character: selectedCharacter });
                setPlayerDead(true);
                if (roomWsRef.current && roomWsRef.current.readyState === WebSocket.OPEN) {
                  roomWsRef.current.send(JSON.stringify({ type: 'game_over', score: scoreRef.current + totalCoinsRef.current }));
                }
              }
              // Don't reset ad unlocks or change gameState — AI/room keeps playing
            } else {
              // Solo mode: check if revive is available
              if (!hasRevivedRef.current && !revivePendingRef.current) {
                // Show revive popup — freeze physics via revivePendingRef
                revivePendingRef.current = true;
                setShowRevivePopup(true);
                setReviveCountdown(5);
                let count = 5;
                reviveTimerRef.current = setInterval(() => {
                  count--;
                  setReviveCountdown(count);
                  if (count <= 0) {
                    clearInterval(reviveTimerRef.current);
                    reviveTimerRef.current = null;
                    revivePendingRef.current = false;
                    setShowRevivePopup(false);
                    // No revive — proceed with game over
                    finalizeSoloDeath();
                  }
                }, 1000);
              } else if (hasRevivedRef.current) {
                // Already revived — proceed with game over
                finalizeSoloDeath();
                return;
              }
            }
          }
          
          // Still alive — bounce UPWARD and start damage shield
          isInvincibleRef.current = true;
          playerVelocityRef.current = JUMP_FORCE * 0.8;
          playerYRef.current = Math.max(playerYRef.current - 30, 50);
          
          if (invincibleTimerRef.current) clearTimeout(invincibleTimerRef.current);
          invincibleTimerRef.current = setTimeout(() => {
            isInvincibleRef.current = false;
          }, 2000);
        } else {
          // Invincible from damage shield — bounce off pipes, but no life loss
          // BUT: if player is actively diving, let them dive through instead of re-bouncing
          if (!isDivingRef.current) {
            playerVelocityRef.current = JUMP_FORCE * 0.6;
            playerYRef.current = Math.max(playerYRef.current - 20, 50);
          }
        }
      }

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState, GAME_HEIGHT, GAME_WIDTH, PIPE_WIDTH, COIN_SIZE, PLAYER_SIZE, JUMP_FORCE, checkCollision, highScore, playCoinSound, playScoreSound, playHitSound, playGameOverSound, playBoostSound, playBalloonPopSound, playTornadoSound, deathCount, updateLevelSettings]);

  // Keyboard and click handlers
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        handleJump();
      }
      // Hold left arrow or 'A' to slow down
      if (e.code === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        if (!isSlowingRef.current) {
          playSlowdownSound();
          slowStartTimeRef.current = Date.now();
        }
        isSlowingRef.current = true;
        setIsSlowing(true);
      }
      // Hold down arrow or 'S' to dive
      if (e.code === 'ArrowDown' || e.key === 's' || e.key === 'S') {
        e.preventDefault();
        isDivingRef.current = true;
        setIsDiving(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        isSlowingRef.current = false;
        setIsSlowing(false);
      }
      if (e.code === 'ArrowDown' || e.key === 's' || e.key === 'S') {
        isDivingRef.current = false;
        setIsDiving(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleJump, playSlowdownSound]);

  // Touch slide-left to slow down
  useEffect(() => {
    if (gameState !== 'playing') return;

    const handleTouchStart = (e) => {
      if (e.touches.length > 0) {
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        isTouchSlideRef.current = false; // reset — we don't know if this is a tap or slide yet
      }
    };

    const handleTouchMove = (e) => {
      if (!touchStartRef.current || e.touches.length === 0) return;
      const deltaX = e.touches[0].clientX - touchStartRef.current.x;
      const deltaY = e.touches[0].clientY - touchStartRef.current.y;
      const totalMove = Math.abs(deltaX) + Math.abs(deltaY);
      
      // If finger moved significantly, this is a slide, not a tap
      if (totalMove > 20) {
        isTouchSlideRef.current = true;
      }
      
      // Sliding left (negative deltaX > 30px threshold) — slow down
      if (deltaX < -30) {
        if (!isSlowingRef.current) {
          playSlowdownSound();
          slowStartTimeRef.current = Date.now();
        }
        isSlowingRef.current = true;
        setIsSlowing(true);
      } else {
        isSlowingRef.current = false;
        setIsSlowing(false);
      }
      // Sliding down (positive deltaY > 30px threshold) — power dive
      if (deltaY > 30) {
        isDivingRef.current = true;
        setIsDiving(true);
      } else {
        isDivingRef.current = false;
        setIsDiving(false);
      }
    };

    const handleTouchEnd = () => {
      touchStartRef.current = null;
      isSlowingRef.current = false;
      setIsSlowing(false);
      isDivingRef.current = false;
      setIsDiving(false);
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gameState, playSlowdownSound]);

  // Ambient background sounds for each level
  useEffect(() => {
    if (gameState !== 'playing') return;

    let intervalId;

    // Play ambient sounds based on current level
    if (currentLevel === 0 || currentLevel === 1 || currentLevel === 3) {
      // Sky, Desert, London - bird sounds
      playBirdAmbience(); // Play immediately
      intervalId = setInterval(() => {
        playBirdAmbience();
      }, 5000); // Every 5 seconds
    } else if (currentLevel === 2) {
      // Jungle - monkey sounds + some birds
      playMonkeyAmbience(); // Play immediately
      playBirdAmbience();
      intervalId = setInterval(() => {
        playMonkeyAmbience();
        setTimeout(() => playBirdAmbience(), 2000);
      }, 6000); // Every 6 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [gameState, currentLevel, playBirdAmbience, playMonkeyAmbience]);

  // Splash screen — play whoosh sound and auto-transition after 2.5s
  const splashReadyRef = useRef(false);
  useEffect(() => {
    if (gameState !== 'splash') {
      splashReadyRef.current = false;
      return;
    }
    // Play a magical reveal sound
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const t = ctx.currentTime;
      // Joyful ascending arpeggio — cheerful "ta-da!" jingle
      const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = i < 3 ? 'triangle' : 'sine';
        osc.frequency.setValueAtTime(freq, t + i * 0.12);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.18, t + i * 0.12);
        gain.gain.linearRampToValueAtTime(i === 3 ? 0.2 : 0.08, t + i * 0.12 + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + (i === 3 ? 0.7 : 0.25));
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t + i * 0.12);
        osc.stop(t + i * 0.12 + (i === 3 ? 0.7 : 0.25));
      });
    } catch (e) {}
    // Prevent instant skip from intro click propagation
    const readyTimer = setTimeout(() => { splashReadyRef.current = true; }, 500);
    const timer = setTimeout(() => setGameState('characterSelect'), 2500);
    return () => { clearTimeout(timer); clearTimeout(readyTimer); };
  }, [gameState]);

  // Floating animation for start screen
  const floatOffset = Math.sin(Date.now() / 300) * 15;

  // Deep link loading screen — skip intro/splash while auto-joining a room
  if (deepLinkPending) {
    return (
      <div
        className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #7dd3fc 100%)' }}
        data-testid="deeplink-loading"
      >
        <div className="text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          <div className="text-5xl mb-4 animate-bounce">🎈</div>
          <div className="text-white text-xl font-bold">Joining Room...</div>
          <div className="text-white/60 text-sm mt-2">Getting everything ready</div>
        </div>
      </div>
    );
  }

  // Intro screen
  if (gameState === 'intro') {
    // Skip intro if disabled in settings (Pro feature)
    if (!introOn) {
      handleIntroEnd();
      return null;
    }
    return (
      <div
        className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden bg-black shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none"
        data-testid="intro-screen"
      >
        <video
          src={introVideoUrl}
          autoPlay
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          onPlay={() => {
            // Play intro sound when video starts playing
            playIntroSound();
          }}
          onEnded={handleIntroEnd}
          onError={(e) => {
            console.error('Video error:', e);
            handleIntroEnd();
          }}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ pointerEvents: 'none' }}
        />
        <div className="absolute bottom-8 right-8 z-10">
          <button
            onClick={() => {
              playIntroSound();
              handleIntroEnd();
            }}
            className="px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-base font-semibold rounded-full transition-all hover:scale-105 active:scale-95"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="skip-intro-button"
          >
            Skip →
          </button>
        </div>
        {/* Click anywhere to skip - also triggers sound */}
        <div 
          className="absolute inset-0 cursor-pointer z-5"
          onClick={() => {
            playIntroSound();
            handleIntroEnd();
          }}
        />
      </div>
    );
  }

  // Splash screen — "Mimi Fly" title reveal after intro
  if (gameState === 'splash') {
    return (
      <div
        className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none flex items-center justify-center cursor-pointer"
        data-testid="splash-screen"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #7dd3fc 100%)' }}
        onClick={() => { if (splashReadyRef.current) setGameState('characterSelect'); }}
      >
        {/* Animated clouds */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute text-6xl opacity-20" style={{ top: '15%', left: '-10%', animation: 'splashCloudDrift 8s linear infinite' }}>☁️</div>
          <div className="absolute text-5xl opacity-15" style={{ top: '35%', right: '-15%', animation: 'splashCloudDrift 12s linear infinite reverse' }}>☁️</div>
          <div className="absolute text-4xl opacity-10" style={{ top: '65%', left: '-5%', animation: 'splashCloudDrift 10s linear infinite', animationDelay: '2s' }}>☁️</div>
        </div>
        {/* Sparkle particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-yellow-300"
              style={{
                top: `${15 + Math.random() * 70}%`,
                left: `${10 + Math.random() * 80}%`,
                opacity: 0,
                animation: `splashSparkle 2s ease-in-out infinite`,
                animationDelay: `${i * 0.25}s`,
              }}
            />
          ))}
        </div>
        {/* Main title */}
        <div className="relative z-10 text-center" style={{ animation: 'splashTitlePop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}>
          <h1 className="text-7xl font-bold tracking-tight"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              color: '#fbbf24',
              textShadow: '0 4px 0 #b45309, 0 8px 20px rgba(0,0,0,0.4), 0 0 40px rgba(251,191,36,0.3)',
              WebkitTextStroke: '2px #92400e',
            }}>
            Mimi Fly
          </h1>
          <div className="mt-3 text-white/50 text-sm" style={{ fontFamily: 'Nunito, sans-serif', animation: 'splashFadeIn 0.8s ease-out 0.8s both' }}>
            Tap to continue
          </div>
        </div>
        <style>{`
          @keyframes splashTitlePop {
            0% { transform: scale(0) rotate(-10deg); opacity: 0; }
            60% { transform: scale(1.15) rotate(2deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          @keyframes splashFadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes splashSparkle {
            0%, 100% { opacity: 0; transform: scale(0); }
            50% { opacity: 0.8; transform: scale(1.5); }
          }
          @keyframes splashCloudDrift {
            0% { transform: translateX(-100px); }
            100% { transform: translateX(calc(100vw + 100px)); }
          }
        `}</style>
      </div>
    );
  }

  // Outro screen (plays before game over)
  if (gameState === 'outro') {
    return (
      <div
        className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden bg-black shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none"
        data-testid="outro-screen"
      >
        <video
          src={outroVideoUrl}
          autoPlay
          muted
          playsInline
          webkit-playsinline="true"
          onPlay={() => {
            // Play outro music
            try {
              const audio = new Audio(outroMusicUrl);
              audio.volume = 0.7;
              audio.play().catch(() => {});
              outroAudioRef.current = audio;
            } catch (e) {}
            playFallingSound();
          }}
          onEnded={handleOutroEnd}
          onError={(e) => {
            console.error('Outro video error:', e);
            handleOutroEnd();
          }}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ pointerEvents: 'none' }}
        />
        <div className="absolute bottom-8 right-8 z-10">
          <button
            onClick={handleOutroEnd}
            className="px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-base font-semibold rounded-full transition-all hover:scale-105 active:scale-95"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="skip-outro-button"
          >
            Skip →
          </button>
        </div>
        {/* Click anywhere to skip */}
        <div 
          className="absolute inset-0 cursor-pointer z-5"
          onClick={handleOutroEnd}
        />
      </div>
    );
  }

  // Login Screen (shown first time playing online)
  if (showLoginScreen) {
    return (
      <LoginScreen
        onGoogleLogin={handleGoogleLogin}
        onGuestLogin={handleGuestLogin}
        onBack={() => { setShowLoginScreen(false); setGameState('characterSelect'); }}
      />
    );
  }

  // Character Selection Screen
  if (gameState === 'characterSelect') {
    // Play with Friends — Create/Join screen
    if (roomScreen === 'create_join') {
      return (
        <PlayWithFriendsScreen
          playerName={selectedCharacter?.name || 'Player'}
          playerCharacter={selectedCharacter?.id || 'mimi'}
          onBack={() => setRoomScreen(null)}
          onRoomJoined={(code, pid) => {
            setRoomCode(code);
            setRoomPlayerId(pid);
            setRoomScreen('lobby');
          }}
        />
      );
    }

    // Play with Friends — Room Lobby
    if (roomScreen === 'lobby' && roomCode && roomPlayerId) {
      return (
        <RoomLobbyWrapper
          roomCode={roomCode}
          playerId={roomPlayerId}
          apiUrl={API_URL}
          playerCharacter={selectedCharacter}
          onLeave={() => {
            setRoomScreen(null);
            setRoomCode(null);
            setRoomPlayerId(null);
          }}
          onGameStart={(ws) => {
            // Transition from lobby to game — bypass auth, go straight to start
            setRoomScreen(null);
            setGameMode('fakeOnline');
            gameModeRef.current = 'fakeOnline';
            // No AI opponents in room play — real players are in the room
            aiPlayersRef.current = [];
            setAiPlayersDisplay([]);
            remotePlayersRef.current = {};
            setRemotePlayersDisplay({});
            allRoomDeadRef.current = false;
            // Initialize seeded RNG from room code — all players in same room get identical pipes
            roomSeedRNGRef.current = createSeededRNG(roomCode);
            // Keep WebSocket for in-game sync
            roomWsRef.current = ws;
            isRoomGameRef.current = true;
            if (ws) {
              ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'game_update') {
                  // Remote player position update
                  remotePlayersRef.current[data.player_id] = data.state;
                } else if (data.type === 'player_finished') {
                  // Remote player died
                  if (remotePlayersRef.current[data.player_id]) {
                    remotePlayersRef.current[data.player_id].alive = false;
                    remotePlayersRef.current[data.player_id].score = data.score;
                  }
                } else if (data.type === 'play_again_status') {
                  // Someone pressed Play Again — update ready count
                  setRoomReadyCount(data.ready_count);
                  setRoomTotalCount(data.total_count);
                } else if (data.type === 'game_restart') {
                  // All players ready — restart the game
                  resetForRoomRestart();
                } else if (data.type === 'player_left') {
                  // A player left the room — remove from remote players
                  if (remotePlayersRef.current[data.player_id]) {
                    delete remotePlayersRef.current[data.player_id];
                    setRemotePlayersDisplay({ ...remotePlayersRef.current });
                  }
                } else if (data.type === 'game_emoji') {
                  // In-game emoji from another player — show floating over their character
                  const feId = `ge_${Date.now()}_${data.player_id}`;
                  setGameFloatingEmojis(prev => [...prev, { id: feId, emoji: data.emoji, playerId: data.player_id }]);
                  setTimeout(() => setGameFloatingEmojis(prev => prev.filter(e => e.id !== feId)), 1500);
                }
              };
            }
            setGameState('start');
          }}
        />
      );
    }

    return (
      <CharacterSelect
        characters={CHARACTERS}
        isPro={isPro}
        adUnlocked={adUnlocked}
        watchAdToUnlock={watchAdToUnlock}
        onSelect={handleCharacterSelect}
        onUnlockPro={(plan) => {
          // Simulated purchase — will be replaced with App Store / Play Store In-App Purchase
          setIsPro(true);
          localStorage.setItem('mimiPro', 'true');
          localStorage.setItem('mimiProPlan', plan);
        }}
        onRestorePurchase={() => {
          // Simulated restore — checks localStorage for previous purchase
          const hasPurchase = localStorage.getItem('mimiPro') === 'true';
          if (hasPurchase) {
            setIsPro(true);
          } else {
            // Show a brief notification that no purchase was found
            const toast = document.createElement('div');
            toast.style.cssText = 'position:fixed;top:60px;left:50%;transform:translateX(-50%);z-index:9999;background:#1e293b;color:#f8fafc;padding:12px 24px;border-radius:12px;font-family:Nunito,sans-serif;font-size:14px;box-shadow:0 4px 20px rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.1);';
            toast.textContent = 'No previous purchase found';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2500);
          }
        }}
        initAudio={initAudio}
        playCoinSound={playCoinSound}
        sfxOn={sfxOn} setSfxOn={setSfxOn}
        isMusicOn={isMusicOn} toggleMusic={toggleMusic}
        vibrationOn={vibrationOn} setVibrationOn={setVibrationOn}
        introOn={introOn} setIntroOn={setIntroOn}
        outroOn={outroOn} setOutroOn={setOutroOn}
        onPlayWithFriends={(character) => {
          setSelectedCharacter(character);
          setRoomScreen('create_join');
        }}
      />
    );
  }

  // Matchmaking Screen
  if (gameState === 'matchmaking') {
    return (
      <MatchmakingScreen
        matchmakingPlayers={matchmakingPlayers}
        cities={matchmakingCitiesRef.current}
        fakeNames={matchmakingNamesRef.current}
        loggedInUser={loggedInUser}
        selectedCharacter={selectedCharacter}
      />
    );
  }

  // Get background gradient based on current level
  const getBackgroundStyle = () => {
    switch (levels[currentLevel]) {
      case 'sky':
        return 'bg-gradient-to-b from-sky-300 via-blue-400 to-cyan-300';
      case 'desert':
        return 'bg-gradient-to-b from-orange-400 via-yellow-500 to-amber-600';
      case 'jungle':
        return 'bg-gradient-to-b from-green-600 via-emerald-600 to-green-800';
      case 'london':
        return 'bg-gradient-to-b from-gray-500 via-slate-400 to-gray-600';
      case 'mountain':
        return 'bg-gradient-to-b from-slate-300 via-blue-200 to-slate-400';
      case 'space':
        return 'bg-gradient-to-b from-indigo-950 via-purple-950 to-slate-950';
      case 'volcano':
        return 'bg-gradient-to-b from-red-900 via-orange-800 to-yellow-900';
      default:
        return 'bg-gradient-to-b from-sky-400 to-sky-200';
    }
  };

  // Get pipe color based on current level
  const getPipeColor = () => {
    switch (levels[currentLevel]) {
      case 'sky':
        return 'bg-white border-gray-200 shadow-lg';
      case 'desert':
        return 'bg-amber-600 border-amber-800';
      case 'jungle':
        return 'bg-green-600 border-green-900';
      case 'london':
        return 'bg-red-600 border-red-900';
      case 'mountain':
        return 'bg-stone-500 border-stone-700';
      case 'space':
        return 'bg-purple-600 border-purple-900';
      case 'volcano':
        return 'bg-red-700 border-red-950';
      default:
        return 'bg-green-500 border-green-800';
    }
  };

  return (
    <div
      className={`relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden ${getBackgroundStyle()} shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none touch-manipulation cursor-pointer`}
      onClick={(e) => {
        // On touch devices, suppress jump if the gesture was a slide (dive/slow-down)
        if (isTouchSlideRef.current) {
          isTouchSlideRef.current = false;
          return;
        }
        handleJump();
      }}
      data-testid="game-screen"
    >
      {/* Clouds - Enhanced for Sky level */}
      {gameObjects.clouds.map((cloud) => (
        <div
          key={cloud.id}
          className={`absolute rounded-full ${currentLevel === 0 ? 'bg-white opacity-90' : 'bg-white opacity-70'}`}
          style={{
            left: `${cloud.x - (gameObjects.cameraX || 0)}px`,
            top: `${cloud.y}px`,
            width: `${cloud.size}px`,
            height: `${cloud.size * 0.6}px`,
            filter: currentLevel === 0 ? 'blur(3px)' : 'blur(2px)',
            boxShadow: currentLevel === 0 ? '0 4px 12px rgba(255,255,255,0.5)' : 'none',
          }}
        />
      ))}

      {/* Level-specific visual effects */}
      <LevelEffects currentLevel={currentLevel} gameState={gameState} frame={frameRef.current} />

      {/* Pipes */}
      {gameObjects.pipes.map((pipe) => {
          const camX = gameObjects.cameraX || 0;
          return (
          <div key={pipe.id} style={{ opacity: balloonBoostActive ? 0.4 : 1, transition: 'opacity 0.3s' }}>
            {/* Top pipe */}
            <div
              className={`absolute ${getPipeColor()} border-4`}
              style={{
                left: `${pipe.x - camX}px`,
                top: 0,
                width: `${PIPE_WIDTH}px`,
                height: `${pipe.topHeight}px`,
              }}
            >
              <div
                className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 ${getPipeColor()} border-4`}
                style={{ width: `${PIPE_WIDTH + 10}px`, height: '30px' }}
              />
            </div>
            {/* Bottom pipe */}
            <div
              className={`absolute ${getPipeColor()} border-4`}
              style={{
                left: `${pipe.x - camX}px`,
                bottom: 0,
                width: `${PIPE_WIDTH}px`,
                height: `${pipe.bottomHeight}px`,
              }}
            >
              <div
                className={`absolute top-0 left-1/2 transform -translate-x-1/2 ${getPipeColor()} border-4`}
                style={{ width: `${PIPE_WIDTH + 10}px`, height: '30px' }}
              />
            </div>
          </div>
          );
        })}

      {/* Coins */}
      {gameObjects.coins.map((coin) => (
          <div
            key={coin.id}
            className="absolute w-8 h-8 rounded-full bg-yellow-400 border-4 border-yellow-600 flex items-center justify-center font-bold text-yellow-700 text-xs coin-spin"
            style={{
              left: `${coin.x - (gameObjects.cameraX || 0)}px`,
              top: `${coin.y}px`,
            }}
          >
            $
          </div>
        ))}

      {/* Balloon Power-ups */}
      {gameObjects.balloons && gameObjects.balloons.map((balloon) => (
        <div
          key={balloon.id}
          className="absolute"
          data-testid="balloon-powerup"
          style={{
            left: `${balloon.x - (gameObjects.cameraX || 0)}px`,
            top: `${balloon.y + Math.sin(Date.now() * 0.003) * 8}px`,
            width: '40px',
            height: '50px',
            fontSize: '36px',
            filter: 'drop-shadow(0 0 6px rgba(168, 85, 247, 0.6))',
            animation: 'float-particle 2s ease-in-out infinite',
          }}
        >
          🎈
        </div>
      ))}

      {/* Tornadoes */}
      {gameObjects.tornadoes && gameObjects.tornadoes.map((tornado) => (
        <div
          key={tornado.id}
          className="absolute"
          data-testid="tornado-hazard"
          style={{
            left: `${tornado.x - (gameObjects.cameraX || 0)}px`,
            top: `${tornado.y}px`,
            width: '40px',
            height: '50px',
            fontSize: '34px',
            filter: 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.7))',
            animation: 'spin 0.8s linear infinite',
          }}
        >
          🌪️
        </div>
      ))}

      {/* Player */}
      {(() => {
        // Per-character visual size scaling to compensate for different art compositions
        // Hitbox stays at PLAYER_SIZE (100px) for all — only visual rendering changes
        const scaleMap = { mimi: 1.1, juma: 1.26, soni: 1.17, sam: 1.3 };
        const charId = selectedCharacter?.id || 'mimi';
        const visualSize = PLAYER_SIZE * (scaleMap[charId] || 1.0);
        const sizeOffset = (visualSize - PLAYER_SIZE) / 2;
        return (
          <img
            src={characterUrl}
            alt={selectedCharacter ? selectedCharacter.name : 'Mimi'}
            className="absolute object-contain character-float"
            data-testid="player-character"
            style={{
              left: gameState === 'playing' ? `${80 - sizeOffset}px` : `${80 - sizeOffset}px`,
              top: gameState === 'start' ? `${250 + floatOffset - sizeOffset}px` : `${playerDisplay.y - sizeOffset}px`,
              width: gameState === 'playing' 
                ? `${visualSize + Math.sin(frameRef.current * 0.2) * 2}px`
                : `${visualSize}px`,
              height: gameState === 'playing'
                ? `${visualSize - Math.sin(frameRef.current * 0.2) * 2}px`
                : `${visualSize}px`,
              transform: gameState === 'playing' 
                ? `rotate(${Math.min(Math.max(playerDisplay.rotation, -30), 30)}deg)`
                : 'rotate(0deg)',
              transition: gameState === 'start' ? 'none' : 'transform 0.1s ease-out',
              opacity: playerDead ? 0 : (isInvincibleRef.current && gameState === 'playing' && frameRef.current % 6 < 3 ? 0.3 : 1),
              filter: isInvincibleRef.current && gameState === 'playing' ? 'drop-shadow(0 0 8px rgba(255,255,255,0.8))' : 'none',
            }}
          />
        );
      })()}

      {/* AI Opponent Characters */}
      {gameState === 'playing' && aiPlayersDisplay.map((ai) => {
        const aiScaleMap = { mimi: 1.1, juma: 1.26, soni: 1.17, sam: 1.3 };
        const aiVisualSize = PLAYER_SIZE * 0.75 * (aiScaleMap[ai.id] || 1.0);
        const camX = gameObjects.cameraX || 0;
        return ai.lives > 0 && (
          <img
            key={ai.id}
            src={ai.gif}
            alt={ai.name}
            className="absolute object-contain"
            data-testid={`ai-player-${ai.id}`}
            style={{
              left: `${(ai.x || 60) - camX}px`,
              top: `${ai.y}px`,
              width: `${aiVisualSize}px`,
              height: `${aiVisualSize}px`,
              transform: `rotate(${Math.min(Math.max(ai.velocity * 3, -25), 25)}deg)`,
              opacity: ai.isInvincible && frameRef.current % 6 < 3 ? 0.3 : 0.85,
              filter: `drop-shadow(0 0 4px ${ai.color})`,
              zIndex: 5,
            }}
          />
        );
      })}

      {/* Remote Room Players */}
      {gameState === 'playing' && Object.entries(remotePlayersDisplay).map(([pid, rp]) => {
        if (!rp.alive) return null;
        const charData = CHARACTERS.find(c => c.id === rp.character) || CHARACTERS[0];
        const rpScaleMap = { mimi: 1.1, juma: 1.26, soni: 1.17, sam: 1.3 };
        const rpSize = PLAYER_SIZE * 0.75 * (rpScaleMap[rp.character] || 1.0);
        return (
          <img
            key={pid}
            src={charData.gif}
            alt={rp.name || 'Player'}
            className="absolute object-contain"
            data-testid={`remote-player-${pid}`}
            style={{
              left: `${(rp.x || 60)}px`,
              top: `${rp.y}px`,
              width: `${rpSize}px`,
              height: `${rpSize}px`,
              transform: `rotate(${Math.min(Math.max((rp.rotation || 0), -25), 25)}deg)`,
              opacity: 0.85,
              filter: `drop-shadow(0 0 6px ${charData.color})`,
              zIndex: 5,
            }}
          />
        );
      })}

      {/* Score, Lives, Music, AI Overlay */}
      {gameState === 'playing' && (
        <GameHUD
          selectedCharacter={selectedCharacter} score={score} coins={coins} lives={lives}
          currentLevel={currentLevel} levelNames={levelNames}
          isMusicOn={isMusicOn} toggleMusic={toggleMusic}
          balloonBoostActive={balloonBoostActive} isSlowing={isSlowing} isDiving={isDiving}
          gameMode={gameMode} aiPlayersDisplay={aiPlayersDisplay}
        />
      )}


      {/* Level Up Notification — prominent center announcement */}
      {showLevelUp && (
        <div className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center">
          <div className="text-center" style={{ animation: 'levelAnnounce 3s ease-out forwards' }}>
            <div className="text-white text-lg font-bold tracking-widest uppercase mb-1" 
              style={{ fontFamily: 'Fredoka, sans-serif', textShadow: '0 2px 8px rgba(0,0,0,0.6)', opacity: 0.8 }}>
              Level {currentLevel + 1}
            </div>
            <div className="text-white text-4xl font-bold" 
              style={{ fontFamily: 'Fredoka, sans-serif', textShadow: '0 0 30px rgba(168,85,247,0.8), 0 4px 12px rgba(0,0,0,0.6)' }}>
              {levelNames[currentLevel]}
            </div>
          </div>
        </div>
      )}

      {/* Start Screen */}
      {gameState === 'start' && (
        <div
          className="absolute inset-0 z-50 flex flex-col items-center"
          data-testid="start-screen"
          style={{
            background: 'linear-gradient(180deg, #4bb8f0 0%, #87ceeb 60%, #4bb8f0 100%)',
            overflow: 'hidden',
          }}
        >
          {/* Animated clouds overlay — CSS realistic clouds */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute" style={{ top: '6%', width: '120px', height: '50px', background: 'radial-gradient(ellipse, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 60%, transparent 100%)', borderRadius: '50%', animation: 'cloudDrift 18s linear infinite', left: '-140px', filter: 'blur(2px)' }} />
            <div className="absolute" style={{ top: '20%', width: '90px', height: '40px', background: 'radial-gradient(ellipse, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.35) 60%, transparent 100%)', borderRadius: '50%', animation: 'cloudDrift 26s linear infinite', animationDelay: '-6s', left: '-120px', filter: 'blur(2px)' }} />
            <div className="absolute" style={{ top: '38%', width: '100px', height: '35px', background: 'radial-gradient(ellipse, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.3) 60%, transparent 100%)', borderRadius: '50%', animation: 'cloudDrift 22s linear infinite', animationDelay: '-12s', left: '-100px', filter: 'blur(3px)' }} />
            <div className="absolute" style={{ top: '52%', width: '110px', height: '45px', background: 'radial-gradient(ellipse, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.3) 60%, transparent 100%)', borderRadius: '50%', animation: 'cloudDrift 30s linear infinite', animationDelay: '-18s', left: '-110px', filter: 'blur(2px)' }} />
          </div>

          {/* Animated birds — flying forward (left to right) */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div style={{ position: 'absolute', top: '12%', fontSize: '16px', animation: 'birdFly 12s linear infinite', left: '-30px' }}>&#128038;</div>
            <div style={{ position: 'absolute', top: '28%', fontSize: '14px', animation: 'birdFly 16s linear infinite', animationDelay: '-5s', left: '-30px' }}>&#128038;</div>
            <div style={{ position: 'absolute', top: '8%', fontSize: '12px', animation: 'birdFly 20s linear infinite', animationDelay: '-10s', left: '-30px' }}>&#128038;</div>
          </div>

          {/* CSS Pipes — static pairs on sides */}
          {/* Left side pipes */}
          <div className="absolute pointer-events-none" style={{ left: '2%', top: 0, bottom: 0, width: '14%' }}>
            <div style={{ position: 'absolute', top: 0, width: '100%', height: '25%', background: 'linear-gradient(90deg, #2d8a2d, #3cb43c, #2d8a2d)', borderRadius: '0 0 6px 6px', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', top: '24%', width: '120%', left: '-10%', height: '4%', background: 'linear-gradient(90deg, #1e6b1e, #2d8a2d, #1e6b1e)', borderRadius: '4px' }} />
            <div style={{ position: 'absolute', bottom: 0, width: '100%', height: '40%', background: 'linear-gradient(90deg, #2d8a2d, #3cb43c, #2d8a2d)', borderRadius: '6px 6px 0 0', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', bottom: '39%', width: '120%', left: '-10%', height: '4%', background: 'linear-gradient(90deg, #1e6b1e, #2d8a2d, #1e6b1e)', borderRadius: '4px' }} />
          </div>

          {/* Left-center pipe — opening/closing */}
          <div className="absolute pointer-events-none" style={{ left: '20%', top: 0, bottom: 0, width: '12%' }}>
            <div style={{ position: 'absolute', top: 0, width: '100%', background: 'linear-gradient(90deg, #cc3333, #e64545, #cc3333)', borderRadius: '0 0 6px 6px', animation: 'pipeOpen 3s ease-in-out infinite', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', bottom: 0, width: '100%', background: 'linear-gradient(90deg, #cc3333, #e64545, #cc3333)', borderRadius: '6px 6px 0 0', animation: 'pipeOpenBottom 3s ease-in-out infinite', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
          </div>

          {/* Right-center pipe — opening/closing (offset timing) */}
          <div className="absolute pointer-events-none" style={{ right: '20%', top: 0, bottom: 0, width: '12%' }}>
            <div style={{ position: 'absolute', top: 0, width: '100%', background: 'linear-gradient(90deg, #cc9900, #e6b800, #cc9900)', borderRadius: '0 0 6px 6px', animation: 'pipeOpen2 4s ease-in-out infinite', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', bottom: 0, width: '100%', background: 'linear-gradient(90deg, #cc9900, #e6b800, #cc9900)', borderRadius: '6px 6px 0 0', animation: 'pipeOpen2Bottom 4s ease-in-out infinite', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
          </div>

          {/* Right side pipes */}
          <div className="absolute pointer-events-none" style={{ right: '2%', top: 0, bottom: 0, width: '14%' }}>
            <div style={{ position: 'absolute', top: 0, width: '100%', height: '35%', background: 'linear-gradient(90deg, #6633cc, #8844ee, #6633cc)', borderRadius: '0 0 6px 6px', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', top: '34%', width: '120%', left: '-10%', height: '4%', background: 'linear-gradient(90deg, #4d1f99, #6633cc, #4d1f99)', borderRadius: '4px' }} />
            <div style={{ position: 'absolute', bottom: 0, width: '100%', height: '30%', background: 'linear-gradient(90deg, #6633cc, #8844ee, #6633cc)', borderRadius: '6px 6px 0 0', boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', bottom: '29%', width: '120%', left: '-10%', height: '4%', background: 'linear-gradient(90deg, #4d1f99, #6633cc, #4d1f99)', borderRadius: '4px' }} />
          </div>

          {/* Far left small pipe */}
          <div className="absolute pointer-events-none" style={{ left: '-3%', top: 0, bottom: 0, width: '10%' }}>
            <div style={{ position: 'absolute', top: 0, width: '100%', height: '45%', background: 'linear-gradient(90deg, #2277aa, #33aadd, #2277aa)', borderRadius: '0 0 6px 6px', boxShadow: 'inset -3px 0 6px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', bottom: 0, width: '100%', height: '25%', background: 'linear-gradient(90deg, #2277aa, #33aadd, #2277aa)', borderRadius: '6px 6px 0 0', boxShadow: 'inset -3px 0 6px rgba(0,0,0,0.2)' }} />
          </div>

          {/* Far right small pipe */}
          <div className="absolute pointer-events-none" style={{ right: '-3%', top: 0, bottom: 0, width: '10%' }}>
            <div style={{ position: 'absolute', top: 0, width: '100%', height: '20%', background: 'linear-gradient(90deg, #cc6633, #ee8844, #cc6633)', borderRadius: '0 0 6px 6px', boxShadow: 'inset -3px 0 6px rgba(0,0,0,0.2)' }} />
            <div style={{ position: 'absolute', bottom: 0, width: '100%', height: '45%', background: 'linear-gradient(90deg, #cc6633, #ee8844, #cc6633)', borderRadius: '6px 6px 0 0', boxShadow: 'inset -3px 0 6px rgba(0,0,0,0.2)' }} />
          </div>

          {/* Grass at the bottom */}
          <div className="absolute bottom-0 inset-x-0 h-12 pointer-events-none" style={{ background: 'linear-gradient(180deg, #4CAF50 0%, #388E3C 100%)' }} />

          {/* Play button centered */}
          <div className="flex-1 flex items-center justify-center relative z-10">
            <button
              className="px-10 py-4 text-white text-2xl font-bold rounded-full shadow-2xl transition-all hover:scale-110 active:scale-95"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                boxShadow: '0 6px 25px rgba(249,115,22,0.5), 0 2px 10px rgba(0,0,0,0.3)',
                animation: 'pulse 2s ease-in-out infinite',
              }}
              data-testid="start-button"
              onClick={handleJump}
            >
              Tap to Fly
            </button>
          </div>

          {/* 3 Kids centered on the grass — gentle bobbing */}
          <div className="absolute bottom-6 inset-x-0 flex justify-center pointer-events-none z-10" style={{ animation: 'kidsFloat 3s ease-in-out infinite' }}>
            <img
              src={KIDS_PNG_URL}
              alt="Characters"
              className="w-72 h-auto object-contain"
              style={{ filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.4))' }}
              draggable={false}
            />
          </div>

          <style>{`
            @keyframes cloudDrift {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(100vw + 200px)); }
            }
            @keyframes birdFly {
              0% { transform: translateX(0) translateY(0) scaleX(-1); }
              25% { transform: translateX(25vw) translateY(-8px) scaleX(-1); }
              50% { transform: translateX(50vw) translateY(0) scaleX(-1); }
              75% { transform: translateX(75vw) translateY(-6px) scaleX(-1); }
              100% { transform: translateX(calc(100vw + 40px)) translateY(0) scaleX(-1); }
            }
            @keyframes kidsFloat {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
            @keyframes pipeOpen {
              0%, 100% { height: 30%; }
              50% { height: 20%; }
            }
            @keyframes pipeOpenBottom {
              0%, 100% { height: 35%; }
              50% { height: 45%; }
            }
            @keyframes pipeOpen2 {
              0%, 100% { height: 20%; }
              50% { height: 32%; }
            }
            @keyframes pipeOpen2Bottom {
              0%, 100% { height: 45%; }
              50% { height: 33%; }
            }
          `}</style>
        </div>
      )}

      {/* Interstitial Ad (shows every 3 deaths) */}
      {showInterstitialAd && (
        <div
          className="absolute inset-0 z-60 flex flex-col items-center justify-center bg-black/90"
          data-testid="interstitial-ad"
        >
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm">
            <div className="text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Advertisement
            </div>
            <div className="bg-gray-200 h-60 w-80 rounded-lg flex items-center justify-center mb-4">
              <div className="text-gray-500 text-center p-4">
                <p className="font-bold mb-2">Ad Space</p>
                <p className="text-sm">Replace with Google AdSense</p>
                <p className="text-xs mt-2">Publisher ID needed</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">Ad closes in 3 seconds...</div>
          </div>
        </div>
      )}

      {/* Continue / Revive Popup */}
      {showRevivePopup && (
        <div
          className="absolute inset-0 z-[55] flex flex-col items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={(e) => { e.stopPropagation(); handleRevive(); }}
          data-testid="revive-popup"
        >
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <p className="text-white text-3xl font-bold mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Continue?
            </p>
            <p className="text-amber-400 text-7xl font-bold mb-8" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {reviveCountdown}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); handleRevive(); }}
              className="px-10 py-4 text-white text-xl font-bold rounded-2xl transition-all hover:scale-105 active:scale-95"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                boxShadow: '0 5px 0 #15803d',
              }}
              data-testid="revive-btn"
            >
              Watch Ad to Continue
            </button>
            <p className="text-white/40 text-xs mt-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              +1 Life &middot; Resume instantly
            </p>
          </div>
        </div>
      )}

      {/* In-Game Emoji Bar — room multiplayer only */}
      {gameState === 'playing' && isRoomGameRef.current && !playerDead && (
        <GameEmojiBar onSendEmoji={handleSendGameEmoji} />
      )}

      {/* Floating Game Emojis — appear over remote players' characters */}
      {gameFloatingEmojis.map((fe) => {
        const remote = remotePlayersRef.current[fe.playerId];
        const screenX = remote ? (remote.x || 80) - cameraXRef.current : 80;
        const screenY = remote ? (remote.y || 250) : 250;
        return (
          <GameFloatingEmoji key={fe.id} emoji={fe.emoji} x={`${screenX}px`} y={`${screenY - 40}px`} />
        );
      })}

      {/* Player Dead Overlay — Quit button when watching AI play */}
      {playerDead && gameState === 'playing' && gameMode === 'fakeOnline' && (
        <PlayerDeadOverlay onQuit={handleQuitOnlineGame} />
      )}

      {/* Rankings Screen — shown after online game ends */}
      {showRankings && gameState === 'rankings' && (
        <RankingsScreen
          rankings={rankings}
          playerWon={rankings.length > 0 && rankings[0]?.isPlayer}
          onDone={handleRankingsDone}
          isRoomMode={isRoomGameRef.current}
          onPlayAgain={handleRoomPlayAgain}
          onGoHome={handleRoomGoHome}
          roomReadyCount={roomReadyCount}
          roomTotalCount={roomTotalCount}
        />
      )}

      {/* Level Unlock Ad Prompt — pauses game for free users at level gate */}
      {showLevelAdPrompt && (
        <div className="absolute inset-0 z-[65] flex items-center justify-center bg-black/70" data-testid="level-ad-prompt">
          <div className="bg-slate-800 rounded-3xl p-6 w-80 shadow-2xl border border-amber-400/20 text-center">
            <div className="text-4xl mb-3">🔒</div>
            <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Level Locked!
            </h2>
            <p className="text-white/50 text-sm mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Jungle, London, Mountain, Space & Volcano
            </p>
            <p className="text-white/40 text-xs mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Watch an ad to unlock all levels for this game
            </p>
            <span className="inline-block text-[10px] font-bold text-amber-400 px-2 py-0.5 rounded-full mb-4"
              style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.2), rgba(245,158,11,0.2))', fontFamily: 'Fredoka, sans-serif' }}>
              PRO FEATURE
            </span>
            <div className="space-y-3">
              <button
                onClick={async () => {
                  await watchAdToUnlock('levels');
                  setShowLevelAdPrompt(false);
                  setCurrentLevel(2);
                  currentLevelRef.current = 2;
                  setShowLevelUp(true);
                  setTimeout(() => setShowLevelUp(false), 3000);
                  setGameState('playing');
                }}
                className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #a855f7, #7c3aed)', fontFamily: 'Fredoka, sans-serif', boxShadow: '0 3px 12px rgba(168,85,247,0.4)' }}
                data-testid="watch-ad-levels"
              >
                📺 Watch Ad to Unlock
              </button>
              <button
                onClick={() => {
                  setShowLevelAdPrompt(false);
                  setGameState('playing');
                }}
                className="w-full py-2 rounded-xl text-xs font-semibold text-white/50 hover:text-white/70 transition-all bg-white/5 hover:bg-white/10"
                style={{ fontFamily: 'Nunito, sans-serif' }}
                data-testid="skip-level-ad"
              >
                Keep playing Level 2
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Overlay */}
      <Leaderboard isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)} currentScore={score} playerName={selectedCharacter?.name} characterId={selectedCharacter?.id} />

      {/* Game Over Screen */}
      {gameState === 'gameOver' && (
        <GameOverScreen
          selectedCharacter={selectedCharacter} score={score} coins={coins} highScore={highScore} isPro={isPro}
          onPlayAgain={handleJump}
          onWatchAd={() => {
            alert('Rewarded Ad would play here! Get Google AdSense Publisher ID to enable real ads. Reward: Continue playing with 1 life!');
            setLives(1);
            livesRef.current = 1;
            isInvincibleRef.current = true;
            setTimeout(() => { isInvincibleRef.current = false; }, 2000);
            setGameState('playing');
            playerYRef.current = 250;
            playerVelocityRef.current = 0;
            frameRef.current = 0;
      cameraXRef.current = 0;
          }}
          onShowLeaderboard={() => setShowLeaderboard(true)}
        />
      )}
    </div>
  );
};

// Room Lobby Wrapper — uses the useRoom hook (must be a separate component for hook rules)
const RoomLobbyWrapper = ({ roomCode, playerId, apiUrl, playerCharacter, onLeave, onGameStart }) => {
  const { room, messages, floatingEmojis, connected, gameStarted, sendMessage, sendReady, sendStartGame, disconnect, getWs } = useRoom(roomCode, playerId, apiUrl);
  const [isReady, setIsReady] = useState(false);

  // When game starts, pass the WebSocket ref to parent
  useEffect(() => {
    if (gameStarted && onGameStart) {
      onGameStart(getWs());
    }
  }, [gameStarted, onGameStart, getWs]);

  const handleReady = (ready) => {
    setIsReady(ready);
    sendReady(ready);
  };

  const handleLeave = () => {
    disconnect();
    onLeave();
  };

  return (
    <RoomLobby
      room={room}
      playerId={playerId}
      messages={messages}
      floatingEmojis={floatingEmojis}
      connected={connected}
      onSendMessage={sendMessage}
      onReady={handleReady}
      onStartGame={sendStartGame}
      onLeave={handleLeave}
      isReady={isReady}
      playerCharacter={playerCharacter}
    />
  );
};

export default MimiFlyGame;